/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IOSAppIcon } from "@/components/IOSAppIcon";
import {
  STORAGE,
  loadDecoys,
  saveDecoys,
  newDecoy,
  pushToTrash,
  t,
  armPeek,
  consumePeek,
  getPeekConfig,
  getLocale,
  getSwiperConfig,
  decodeClockValue,
  decodeClockCard,
  setSwiperCard,
  setSwiperCustomWord,
  readSwiperCard,
  clearSwiperCard,
  getAcrosticCard,
  isAcrosticEnabled,
  applyAcrosticOverrides,
  clearAcrostic,
  getComposeTitle,
  getTenOutsConfig,
  getTridentConfig,
  getSearchForceConfig,
  getSearchForceArmed,
  getSearchForceAnswers,
  getSearchForceFrozenDate,
  formatSearchForceNoteDate,
  setSearchForcePersisted,
  getSearchForcePersisted,
  buildSearchForceBody,
  getApocalypseConfig,
  readApocalypseArmed,
  clearApocalypseArmed,
  consumeApocalypseRehearsing,
  playAppNotification,
  playIphoneLockClick,
  playErrorTritone,
  formatApocalypseCard,
  apocalypseCardLabel,
  triggerPermissionPromptFx,
  speakRevealFx,
  cancelSpeechFx,
  startThemeColorFlickerFx,
  getFlipForceConfig,
  getFlipForceStage,
  setFlipForceStage,
  resetFlipForceStage,
  hapticTap,
} from "@/lib/mr";

// --- Clock-swipe gesture tuning ---------------------------------------------
// A "transmit flick" is a quick directional gesture distinct from a slow drag
// or accidental finger lift. We require:
//   • ≥ MIN_DIST px of net displacement (filters out taps & micro-jitters)
//   • ≤ MAX_DURATION ms from touchstart to touchend (filters out slow drags)
//   • clearly horizontal OR vertical dominance (avoids ambiguous diagonals)
// After a flick is captured, IGNORE_AFTER_MS deduplicates accidental double
// fires from the same finger-roll.
const MIN_DIST = 40;
const MAX_DURATION = 500;
const DOMINANCE = 1.4;
const IGNORE_AFTER_MS = 250;

/**
 * Format a Date in the app's active locale using a real calendar-day
 * timestamp (never the "Today" / "Yesterday" shortcut). Used for the
 * `(date)` placeholder substitution in the Search-Force body so the
 * prediction reads like a note the magician jotted down on a specific
 * date/time — e.g. "14 april 10:31" (nl) / "April 14 10:31 AM" (en) —
 * regardless of how close that timestamp is to `now`.
 */
function formatSearchForceInlineDate(when, locale, langCode) {
  const then = when instanceof Date ? when : new Date(when);
  const time = then.toLocaleTimeString(locale, {
    hour: "2-digit", minute: "2-digit",
  });
  // Day + month, no year — matches the tone of a casual personal note.
  // Example nl: "14 april". Example en: "April 14".
  const dayMonth = then.toLocaleDateString(locale, {
    day: "numeric", month: "long",
  });
  // Language-specific "at" preposition — mirrors iOS Notes verbiage
  // like "14 april om 10:31" (nl) / "April 14 at 10:31 AM" (en).
  const AT_WORD = {
    nl: "om", en: "at", de: "um", fr: "à", es: "a las", it: "alle",
    pt: "às", ru: "в", pl: "o", uk: "о",
  };
  const at = AT_WORD[langCode] || "at";
  // Suppress the preposition path completely — the user asked for a
  // compact "14 april 10:31" style with a single space between day-
  // month and clock. Kept the AT_WORD map above for potential future
  // per-language toggles.
  void at;
  return `${dayMonth} ${time}`;
}

/**
 * Split-out day and time helpers for the `(day)` and `(time)` search-
 * force placeholders. Magicians can now compose sentences like
 * "op (day) om (time) kom ik iemand tegen" without hard-coding both
 * halves into a single opaque `(date)` blob.
 */
function formatSearchForceDayPart(when, locale) {
  const then = when instanceof Date ? when : new Date(when);
  return then.toLocaleDateString(locale, { day: "numeric", month: "long" });
}
function formatSearchForceTimePart(when, locale) {
  const then = when instanceof Date ? when : new Date(when);
  return then.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}
function formatSearchForceWeekdayPart(when, locale) {
  const then = when instanceof Date ? when : new Date(when);
  return then.toLocaleDateString(locale, { weekday: "long" });
}

/**
 * Apple-Notes folder list disguise. Renders a realistic list of mundane
 * "notes" the magician has pre-configured in Settings.
 *
 * Entry-point routing (locked in the Feb 2026 restructure):
 *   • Top row    → Swiper prediction (clock-swipe cards OR 4-direction custom words)
 *   • + button   → Multi-question compose note
 *   • Other rows → Plain compose note (no AI)
 *
 * Triple-tap on the header title quietly exits to Setup so the magician
 * can get back to the control panel without ever showing the audience.
 */
export default function DecoyNotes() {
  const navigate = useNavigate();
  const location = useLocation();
  // When the spectator opens a note from the search results and then
  // taps "< Notes" to come back, iOS-native Notes preserves the
  // search bar exactly as they left it. Restore that behaviour by
  // seeding `searchQuery` from the `returnSearch` field that Trick.jsx
  // pipes back through router state — same string they were typing
  // when they opened the row. Purely a first-render seed; subsequent
  // typing is owned by setSearchQuery below.
  const initialSearchQuery = (() => {
    const rs = location.state && location.state.returnSearch;
    return typeof rs === "string" ? rs : "";
  })();
  const [decoys, setDecoys] = useState(() => loadDecoys());
  const [langCode] = useState(() => localStorage.getItem(STORAGE.LANGUAGE) || "auto");
  // iCloud "storage almost full" cosmetic banner under the counter.
  // Default ON — magician can disable from Settings for a cleaner look.
  const [iCloudBannerEnabled] = useState(() => {
    const v = localStorage.getItem(STORAGE.ICLOUD_BANNER_ENABLED);
    return v === null ? true : v === "1";
  });
  // ─── Search bar (realism only) ───────────────────────────────────────
  // Spectators occasionally tap the search field to "look around". The
  // text input filters the decoy list by title + preview + body. When
  // the query is non-empty, group headers and apocalypse-spawned extras
  // are hidden so the layout matches real iOS Notes search results.
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const searchInputRef = useRef(null);
  // Native-iOS bottom search bar behaviour: on focus the pill glides
  // up to sit just under the top nav and a "Cancel" button appears.
  // On blur / cancel it slides back down. Purely UI state — the
  // filtering itself is driven by `searchQuery`. Seeds `true` when
  // we return from a note with an active query so the "in-search"
  // affordance is preserved across in-app navigation (matches iOS).
  const [searchFocused, setSearchFocused] = useState(() => initialSearchQuery.length > 0);
  const trimmedSearch = searchQuery.trim();
  const isSearchActive = trimmedSearch.length > 0;
  // iOS Safari keyboard tracker — `visualViewport` is the only reliable
  // way to know how tall the software keyboard actually is. We use it
  // to pin the search bar *directly above* the keyboard while the user
  // types (matches native iOS Notes). Without this the bar drifts with
  // the layout viewport and can end up scrolled off-screen when Safari
  // auto-scrolls the focused input into view — that was the "I can't
  // see what I'm searching / typing" bug.
  const [kbOffset, setKbOffset] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const refresh = () => {
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbOffset(kb);
    };
    vv.addEventListener("resize", refresh);
    vv.addEventListener("scroll", refresh);
    refresh();
    return () => {
      vv.removeEventListener("resize", refresh);
      vv.removeEventListener("scroll", refresh);
    };
  }, []);
  const swiperConfig = useRef(getSwiperConfig()).current;
  const tenOutsConfig = useRef(getTenOutsConfig()).current;
  const tridentConfig = useRef(getTridentConfig()).current;
  const searchForceConfig = useRef(getSearchForceConfig()).current;
  const searchForceArmed = useRef(getSearchForceArmed()).current;
  const searchForceAnswers = useRef(getSearchForceAnswers()).current;
  // Session-persistent snapshot of the prediction. Once the spectator
  // opens the search-force row we mirror it into this state so the
  // folder list can inject the note into its correct day-bucket. Re-
  // reads on our custom `mr-search-force-persisted` event AND on the
  // native `storage` event (multi-tab / iframe safety).
  const [searchForcePersisted, setSearchForcePersistedState] = useState(
    () => getSearchForcePersisted(),
  );
  useEffect(() => {
    const refresh = () => setSearchForcePersistedState(getSearchForcePersisted());
    const onStorage = (e) => {
      if (!e || e.key === "mr_search_force_persisted") refresh();
    };
    window.addEventListener("mr-search-force-persisted", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("mr-search-force-persisted", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  const apocalypseCfg = useRef(getApocalypseConfig()).current;

  // --- Notes Apocalypse — three-phase cinematic reveal:
  //   1) "spawn"   : 10 innocent-looking extra notes materialise one by
  //                  one at the top of the list (every 250ms). Phone
  //                  looks like it's "generating" notes by itself.
  //   2) "flicker" : screen pulses + RGB chromatic aberration + hue
  //                  rotation. Note text starts color-shifting. Feels
  //                  like the phone is being hacked in real time.
  //   3) "resolve" : every extra row scrambles and settles into the
  //                  magician's forced sentence. Stays locked until
  //                  the spectator SHAKES the phone, which snaps
  //                  everything back to the clean decoy folder.
  //
  // The three phase-state pieces (`apocalypsePhase`, `apocalypseExtras`,
  // `apocalypseTitles`) are intentionally separate so a single phase
  // change doesn't re-render everything else mid-animation.
  const [apocalypsePhase, setApocalypsePhase] = useState("idle"); // idle|wipe|spawn|flicker|resolve|error|reGlitch|haunt|emptied|flashing
  const [apocalypseExtras, setApocalypseExtras] = useState([]);   // array of {id,title} prepended above real decoys
  const [apocalypseTitles, setApocalypseTitles] = useState([]);
  // Pass-4: post-reveal iOS-style "Error" alert overlay. Renders ABOVE
  // the still-flickering list once resolve scrolls to top. Taps + scroll
  // are blocked by a transparent backdrop; only clock-swipe gestures
  // bubble through to the outer onTouchStart/End handler so the magician
  // can encode a SECOND card during this beat. Dismissed via spectator
  // shake (one-shake mode: clean reset; two-shake mode: re-glitch into
  // the second prediction, then a second shake cleans up).
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  // Bumped on every OK-button tap to retrigger the dialog's jitter
  // keyframe — gives the spectator a "tap does nothing" hapticless
  // feedback while staying unresponsive.
  const [errorJitterKey, setErrorJitterKey] = useState(0);
  // Mirrors apocalypsePhase so the shake handler closure (registered
  // via window.addEventListener) can read the CURRENT phase instead of
  // its stale captured value.
  const apocalypsePhaseRef = useRef("idle");
  useEffect(() => {
    apocalypsePhaseRef.current = apocalypsePhase;
  }, [apocalypsePhase]);
  // Tracks whether the two-shake-mode re-glitch beat has already fired
  // for this performance. After it fires once, the next shake performs
  // the clean reset (so the magician can't accidentally loop forever).
  const reGlitchUsedRef = useRef(false);
  // Phase-0 wipe: per-decoy truncated title (string) during the
  // reverse-hack delete; once all are empty `apocalypseWiped` flips on
  // and the whole preset list disappears from the render.
  const [wipeTitles, setWipeTitles] = useState([]);
  const [apocalypseWiped, setApocalypseWiped] = useState(false);
  // Counter override during the Apocalypse "reload" beat. When non-null,
  // this replaces the natural notes-count computation shown under the
  // "Notes" H1. Flow: at wipe start we snap it to 0 (matches the empty
  // list). Over the spawn window it climbs one integer at a time until
  // it lands back on the pre-glitch total (e.g. 166), even though only
  // ~EXTRA_COUNT haunt rows are actually rendered. Sells the illusion
  // that the entire library was destroyed AND then dragged back from
  // the void — without asking the magician to sit through 166 real
  // spawn animations.
  const [apocalypseCounterOverride, setApocalypseCounterOverride] = useState(null);
  const apocalypseTimers = useRef([]);
  // Wallpaper hand-off: when we just navigated in from /lock with the
  // trick armed, briefly cover the white Notes surface with the SAME
  // wallpaper the lockscreen was showing, then fade it out. Without
  // this the spectator sees a "wallpaper → white flash" transition
  // that screams "this isn't a real lockscreen". The wallpaper handoff
  // bridges those two screens visually so the spectator never sees the
  // app actually mount.
  const [unlockBackdrop, setUnlockBackdrop] = useState(() => {
    if (!apocalypseCfg.wallpaper) return null;
    const armed = readApocalypseArmed();
    if (!armed) return null;
    // Only show on a FRESH arm (within 2s) so reloads on /notes don't
    // re-trigger the handoff unexpectedly.
    if (armed.armedAt && Date.now() - armed.armedAt > 2000) return null;
    return apocalypseCfg.wallpaper;
  });
  const [unlockBackdropFading, setUnlockBackdropFading] = useState(false);
  // Pre-wipe cue dot: a barely-visible signal the magician glances at
  // to know the wipe is N seconds away. Toggles on `delay - 3000ms`
  // after arm and off at wipe-start. Spectator-imperceptible at normal
  // viewing distance.
  const [showCueDot, setShowCueDot] = useState(false);
  // Swiper lock — when true, the notes list is frozen (no scrolling)
  // so the magician can safely execute Swiper gestures without the
  // page scrolling under their finger. Toggled by tapping the native
  // iOS "…" (ellipsis) header button. Auto-clears the instant a card
  // is successfully captured so normal scrolling immediately resumes.
  // A near-invisible cue dot next to the note counter mirrors the
  // Apocalypse pattern so the magician can glance-confirm state.
  const [swiperLocked, setSwiperLocked] = useState(false);
  // Acrostic Notes — armed via the lockscreen flashlight quick-action
  // + passcode entry. When a card is armed, a middle-of-list run of
  // titles is quietly rewritten so their first letters spell the card
  // (e.g. 7♥ → SEVENHEARTS). The state below just tracks the armed
  // card so we can rebuild the override map when it changes.
  const [acrosticCard, setAcrosticCardState] = useState(() => getAcrosticCard());
  useEffect(() => {
    const refresh = () => setAcrosticCardState(getAcrosticCard());
    // Same-tab custom event covers writes from LockScreen without
    // needing a full storage-event round-trip (which only fires
    // cross-tab). `storage` listener stays as a belt-and-braces
    // for the multi-tab case some magicians use during setup.
    window.addEventListener("mr-acrostic-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("mr-acrostic-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  // Auto-scroll: ref to the notes scroll container so each spawned
  // extra can be brought into view as the list grows past the fold.
  const listScrollRef = useRef(null);
  // ─── Search results visibility guard ────────────────────────────
  // When the search transitions into "active" (spectator has typed
  // 1+ chars), snap ONLY the internal notes-list container back to
  // scrollTop=0 so the filtered results render right below the sticky
  // "Notes" header — which is where iOS Notes puts them natively.
  // Without this, if the spectator had scrolled deep into the pre-
  // populated decoy folder before opening search, the filtered
  // results would appear at whatever scrollTop the container was
  // left at, invisible below the fold ("results still scrolled way
  // up" report).
  //
  // We intentionally do NOT touch `window.scrollY` or the layout
  // viewport here — an earlier version did, but that fought iOS
  // Safari's own keyboard-open auto-scroll on every keystroke and
  // caused typed characters to disappear / the input to blur.
  useEffect(() => {
    if (!isSearchActive) return;
    if (listScrollRef.current) {
      listScrollRef.current.scrollTop = 0;
    }
  }, [isSearchActive]);
  // Post-shake reset overlay: a single 0..1 opacity-driven white div
  // that pulses N times when `apocalypseCfg.resetPreset === "flash"`.
  // The actual blink sequence is timer-driven inside runFlashReset().
  const [flashOn, setFlashOn] = useState(false);
  // Post-restore "1-second flash" — once the trick has fully reset and
  // the clean Notes folder is back on screen, the preset decoy titles
  // briefly display the SECOND prediction sentence before flashing
  // back to their original values. Empty string = no override active.
  const [restoreFlashTitle, setRestoreFlashTitle] = useState("");
  // Fake iOS push-notification banners overlay. Each entry is
  // {id, app, title, body}. Rendered as a stack at the top of the
  // screen; new ones slide in, old ones slide out after their
  // configured lifetime.
  const [pushBanners, setPushBanners] = useState([]);
  // Native-FX cleanup: theme-color flicker returns a stop fn we must
  // remember so we can restore the original status-bar color on shake
  // reset / unmount / phase end.
  const themeStopRef = useRef(null);
  // Dedicated <audio ref> for the interference bed. Rendered as an actual
  // DOM element (see JSX below) so iOS Safari treats it as first-class
  // audio — no autoplay policy weirdness, no Web Audio context quirks.
  // Started at wipe timer, stopped right before the error dialog.
  const interferenceRef = useRef(null);
  // Dedicated <audio ref> for the vibration bed. Same "iPhone-in-silent-
  // mode" buzz strategy as the interference bed but shorter and looped —
  // starts alongside the wipe, stops instantly when the error dialog
  // raises. Because iOS Safari blocks the Vibration API, audio is the
  // only channel that reliably fires on all platforms.
  const vibrationRef = useRef(null);
  // Shake-to-reset state. `peakCount` is the number of net-accel
  // deltas above threshold within the last `WINDOW_MS`. `lastMag` is
  // the previous frame's magnitude so we can compute the delta.
  const apocalypseShake = useRef({
    handler: null,
    lastMag: 0,
    peakCount: 0,
    windowTimer: null,
  });
  const clearApocalypseTimers = () => {
    apocalypseTimers.current.forEach((id) => clearTimeout(id));
    apocalypseTimers.current = [];
    // Any pending crash cleared → kill the interference bed too so a
    // shake-reset / triple-tap exit doesn't leave background static
    // running in the void.
    try {
      const iEl = interferenceRef.current;
      if (iEl && !iEl.paused) {
        try { iEl.pause(); } catch { /* ignore */ }
        try { iEl.currentTime = 0; } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    // Same clean-shutdown for the vibration bed on any timer-clear
    // path (shake-reset, triple-tap exit, unmount). Prevents a
    // buzz-in-the-void when the crash sequence is aborted mid-flow.
    try {
      const vEl = vibrationRef.current;
      if (vEl && !vEl.paused) {
        try { vEl.pause(); } catch { /* ignore */ }
        try { vEl.currentTime = 0; } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  };

  // Custom-duration smooth scroll. Used by the resolve loop so the
  // magician can dial the per-row scroll cadence from Settings →
  // Apocalypse → "Reveal scroll speed". `durationMs === 0` means an
  // instant snap (no animation). For non-zero values we run a quick
  // ease-out via requestAnimationFrame.
  const animateScrollToRow = (container, row, durationMs) => {
    if (!container || !row) return;
    const targetTop = Math.max(
      0,
      row.offsetTop - (container.clientHeight - row.clientHeight) / 2
    );
    if (durationMs <= 0) {
      container.scrollTop = targetTop;
      return;
    }
    const startTop = container.scrollTop;
    const delta = targetTop - startTop;
    if (Math.abs(delta) < 1) return;
    const startedAt = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - startedAt) / durationMs);
      container.scrollTop = startTop + delta * easeOut(t);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // Auto-scroll: each new spawned extra brings the bottom of the
  // list into view so the spectator literally watches the list grow
  // past the fold.
  //
  // The `mr-apoc-extra-in` keyframe no longer animates max-height
  // (only opacity + translateY), so each new <li> contributes to
  // scrollHeight the moment it mounts. A single rAF defer is enough
  // to let React commit the DOM before we read scrollHeight.
  //
  // Dep is INTENTIONALLY only apocalypseExtras.length — including
  // apocalypsePhase here caused the spawn→flicker transition to
  // clear the pending scroll (see iteration_15 RCA).
  useEffect(() => {
    if (apocalypseExtras.length === 0) return;
    const el = listScrollRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } catch {
        el.scrollTop = el.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [apocalypseExtras.length]);

  // Wallpaper backdrop fade: trigger right after mount so the white
  // Notes surface only reveals after a soft crossfade from the
  // lockscreen wallpaper.
  useEffect(() => {
    if (!unlockBackdrop) return;
    // One frame to apply opacity:1, then start the transition to 0.
    const id1 = requestAnimationFrame(() => setUnlockBackdropFading(true));
    // After the 600ms transition completes, drop the node entirely so
    // it can never interfere with later phases (e.g. the wipe).
    const id2 = setTimeout(() => setUnlockBackdrop(null), 650);
    return () => {
      cancelAnimationFrame(id1);
      clearTimeout(id2);
    };
  }, [unlockBackdrop]);

  // Preview override for the swiper row (top): shows "head + … + tail"
  // of the prediction template, so the folder list and the open note
  // tell the same story.
  const swiperTopPreview = useMemo(() => {
    if (!swiperConfig.enabled) return null;
    const tpl =
      swiperConfig.mode === "custom" ? swiperConfig.template :
      swiperConfig.format === "custom" ? swiperConfig.template :
      "";
    if (!tpl) return null;
    const idx = tpl.indexOf("(xxx)");
    if (idx < 0) return null;
    const head = tpl.slice(0, idx).replace(/\s+$/, "");
    const tail = tpl.slice(idx + 5).replace(/^\s+/, "");
    if (!head && !tail) return "…";
    if (!head) return `… ${tail}`;
    if (!tail) return `${head} …`;
    return `${head} … ${tail}`;
  }, [swiperConfig]);

  // Re-read decoys whenever the page becomes visible again (e.g. after Settings)
  useEffect(() => {
    const onVis = () => { if (!document.hidden) setDecoys(loadDecoys()); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const locale = getLocale(langCode);
  const isRTL = /^(?:ar|he|fa|ur)/.test(langCode || "");
  const folderLabel = t("notes", langCode);

  // Persist the search-force body the moment the result RENDERS
  // (not only when the spectator taps it). Rationale: sometimes a
  // spectator just reads the preview in the search results, closes
  // Search, and never opens the note — but the magician still wants
  // the full body cached in the Notes list until either (a) the
  // camera quick-action arms a fresh trick, or (b) the magician
  // returns to the admin Settings screen. Mirrors the arming / query
  // conditions from the groupedDecoys memo so we don't drift.
  useEffect(() => {
    const q = (searchQuery || "").trim();
    const useLockArm = !!searchForceArmed;
    const active = (useLockArm || searchForceConfig.enabled) && q.length >= 2;
    if (!active) return;
    const armedCard = useLockArm ? searchForceArmed.card : null;
    let cardLabelLocalized = "";
    if (armedCard) {
      if (typeof armedCard === "string") cardLabelLocalized = armedCard;
      else {
        try { cardLabelLocalized = apocalypseCardLabel(armedCard, langCode) || ""; }
        catch { cardLabelLocalized = ""; }
      }
    }
    const offset = Number.isFinite(searchForceConfig.dateOffsetMinutes)
      ? searchForceConfig.dateOffsetMinutes : 0;
    const frozenDate = getSearchForceFrozenDate();
    const whenDate = frozenDate || new Date(Date.now() - offset * 60 * 1000);
    const dateStr = formatSearchForceInlineDate(whenDate, locale, langCode);
    const dayStr = formatSearchForceDayPart(whenDate, locale);
    const timeStr = formatSearchForceTimePart(whenDate, locale);
    const weekdayStr = formatSearchForceWeekdayPart(whenDate, locale);
    const body = buildSearchForceBody(
      searchForceConfig.template,
      q,
      cardLabelLocalized,
      dateStr,
      searchForceAnswers,
      dayStr,
      timeStr,
      weekdayStr,
    );
    if (!body) return;
    const configuredDate = searchForceConfig.dateISO
      ? new Date(searchForceConfig.dateISO) : null;
    const noteDate = (configuredDate && Number.isFinite(configuredDate.getTime()))
      ? configuredDate : whenDate;
    setSearchForcePersisted({
      title: searchForceConfig.title || "Prediction",
      body,
      dateISO: noteDate.toISOString(),
      spectator: q,
      cardLabel: cardLabelLocalized,
    });
  }, [searchQuery, langCode, locale, searchForceConfig, searchForceArmed, searchForceAnswers]);

  // Backdate (minutes) — drives the special-row timestamps so the
  // prediction notes already look pre-written.
  const [backdateMin] = useState(() => {
    if (!swiperConfig.enabled) return 0;
    const baseRaw = localStorage.getItem(STORAGE.BACKDATE_MIN);
    return baseRaw == null ? 0 : Math.max(0, Math.min(180, parseInt(baseRaw, 10) || 0));
  });

  // Stable trick "role" per decoy row, computed from position AMONG
  // non-userCreated decoys. Without this, a spectator-composed note
  // prepended via the (+) button would shift the swiper / trident /
  // tenouts triggers off their expected rows. Role is used by both
  // the date-inference logic (swiper row → yesterday, userCreated →
  // today) and the openRow routing further below.
  const roleByIndex = useMemo(() => {
    const roles = new Array(decoys.length).fill(null);
    let presetIdx = 0;
    decoys.forEach((n, i) => {
      if (n.userCreated) return;
      if (presetIdx === 0) roles[i] = "swiper";
      else if (presetIdx === 1) roles[i] = "trident";
      else if (presetIdx === 2) roles[i] = "tenouts";
      presetIdx += 1;
    });
    return roles;
  }, [decoys]);

  // Preset-only ordinal per row (0 = swiper, 1 = trident, 2 = tenouts,
  // 3+ = plain preset decoys). Used for the "n days ago" cascade so
  // moving rows around (or inserting userCreated notes) keeps each
  // preset note's apparent age stable.
  const presetIdxByIndex = useMemo(() => {
    const arr = new Array(decoys.length).fill(-1);
    let p = 0;
    decoys.forEach((n, i) => {
      if (n.userCreated) return;
      arr[i] = p;
      p += 1;
    });
    return arr;
  }, [decoys]);

  // Fake date labels per row:
  //   1. If the magician set an explicit datetime in Settings → use it
  //      verbatim.
  //   2. userCreated (+) notes → always "now" so they group under Today.
  //   3. The Swiper prediction row → always one day earlier than now,
  //      with optional backdateMin minutes of clock-time precision, so
  //      it groups under "Yesterday" exactly as a pre-written note would.
  //   4. Other preset rows cascade older by their preset-index (trident
  //      = 2 days ago, tenouts = 3 days ago, …) — no preset ever lands
  //      in "Today", matching Apple Notes' natural staleness.
  const inferredDates = useMemo(() => {
    const now = new Date();
    return decoys.map((n, i) => {
      if (n.time) {
        const d = parseDecoyTime(n.time);
        if (d) return d;
      }
      const d = new Date(now);
      if (n.userCreated) {
        return d;
      }
      const role = roleByIndex[i];
      if (role === "swiper") {
        d.setDate(now.getDate() - 1);
        if (swiperConfig.enabled && backdateMin > 0) {
          d.setTime(d.getTime() - backdateMin * 60 * 1000);
        }
        return d;
      }
      const presetIdx = presetIdxByIndex[i];
      const offsetDays = presetIdx >= 0 ? presetIdx + 1 : i;
      d.setDate(now.getDate() - offsetDays);
      return d;
    });
  }, [decoys, backdateMin, swiperConfig.enabled, roleByIndex, presetIdxByIndex]);

  const dateLabels = useMemo(() => {
    const now = new Date();
    return decoys.map((n, i) => {
      const d = inferredDates[i];
      // Single rule: derive the row prefix from the row's actual date.
      // Keeps the row label and its containing group header in sync —
      // a Today row shows clock time, Yesterday rows show "Yesterday",
      // older rows show the weekday or short month-day.
      return formatRowDateLabel(d, now, locale);
    });
  }, [decoys, inferredDates, locale]);

  // ─── iOS Notes-style grouping ────────────────────────────────────────
  // Apple Notes splits the folder list into bold section headers:
  // Today / Yesterday / Previous 7 Days / Previous 30 Days /
  // [Month Year] (one header per older month). We bucket each decoy
  // by its effective date while preserving its ORIGINAL INDEX so the
  // trick-routing logic (resolved via roleByIndex above) and per-row
  // data-testids stay completely untouched.
  const groupedDecoys = useMemo(() => {
    const now = new Date();
    // ─── Search filter ───────────────────────────────────────────────
    // When a query is present, return a single flat group with no
    // header label. This mirrors iOS Notes' search results UI which
    // collapses date groupings and shows a single results list.
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const matches = [];
      decoys.forEach((n, i) => {
        const haystack = `${n.title || ""} ${n.preview || ""} ${n.body || ""}`.toLowerCase();
        if (haystack.includes(q)) matches.push({ n, i });
      });
      // --- Search-Force injection ---------------------------------
      // Two arm sources with different body semantics but a SHARED
      // note-metadata date so the note always reads as pre-written:
      //   1. Lockscreen-armed (camera quick-action) — (card) in the
      //      body is substituted with the freshly-decoded card.
      //   2. Settings-armed toggle — (card) collapses to empty.
      // In BOTH cases (date) in the body is substituted with a
      // localized `now - dateOffsetMinutes` timestamp so the sentence
      // can weave in a fresh time reference (e.g., "op 14 april 10:31
      // iemand tegenkom…").
      //
      // Match-delay: the synth prediction row is only injected once
      // the spectator has typed AT LEAST 2 characters. Firing it on
      // the very first keystroke felt too eager (single-letter search
      // in real iOS Notes usually returns dozens of unrelated hits or
      // nothing, not a perfectly personalised prediction). A 2-char
      // threshold matches the natural pause point where a spectator
      // has committed to typing a name rather than just tapping.
      const useLockArm = !!searchForceArmed;
      const searchForceActive =
        (useLockArm || searchForceConfig.enabled) && q.length >= 2;
      if (searchForceActive) {
        const armedCard = useLockArm ? searchForceArmed.card : null;
        // Localize the card label in the app's active language when it
        // was stored as a {value,suit} object. Legacy plain-string
        // labels fall through as-is.
        const cardLabelLocalized = (() => {
          if (!armedCard) return "";
          if (typeof armedCard === "string") return armedCard;
          try {
            return apocalypseCardLabel(armedCard, langCode) || "";
          } catch { return ""; }
        })();
        const offset = Number.isFinite(searchForceConfig.dateOffsetMinutes)
          ? searchForceConfig.dateOffsetMinutes
          : 0;
        // Prefer the arm-time frozen timestamp so the (date) reads
        // exactly the same for the entire session — a spectator who
        // taps the note two hours later still sees the same clock.
        // Falls back to live `now - offset` when no freeze exists
        // (upgrade path / non-armed Settings-toggle mode).
        const frozenDate = getSearchForceFrozenDate();
        const whenDate = frozenDate || new Date(Date.now() - offset * 60 * 1000);
        const dateStr = formatSearchForceInlineDate(
          whenDate,
          locale,
          langCode,
        );
        // Split copies for the new `(day)` and `(time)` placeholders
        // so the magician can write natural sentences like
        // "op (day) om (time) kom ik iemand tegen…".
        const dayStr = formatSearchForceDayPart(whenDate, locale);
        const timeStr = formatSearchForceTimePart(whenDate, locale);
        const weekdayStr = formatSearchForceWeekdayPart(whenDate, locale);
        const body = buildSearchForceBody(
          searchForceConfig.template,
          searchQuery,
          cardLabelLocalized,
          dateStr,
          searchForceAnswers,
          dayStr,
          timeStr,
          weekdayStr,
        );
        const firstLine = body.split("\n").find((l) => l.trim().length > 0) || "";
        // The search-mode row shows a natural date-label like "14 apr
        // om 10:31" derived from the CONFIGURED note date. Falls back
        // to the legacy free-form dateLabel string when no structured
        // date is set.
        const configuredDate = searchForceConfig.dateISO
          ? new Date(searchForceConfig.dateISO)
          : null;
        const configuredDateLabel = configuredDate && Number.isFinite(configuredDate.getTime())
          ? formatSearchForceNoteDate(configuredDate, locale, langCode)
          : "";
        const synth = {
          id: "__search_force__",
          title: searchForceConfig.title || "Prediction",
          preview: firstLine.slice(0, 80),
          body,
          isSearchForce: true,
          dateLabel: configuredDateLabel || searchForceConfig.dateLabel,
        };
        matches.unshift({ n: synth, i: -1, isSearchForce: true });
      }
      if (matches.length === 0) return [];
      return [{ id: "search", label: "", items: matches, isSearch: true }];
    }
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const pinned = [];
    const today = [];
    const yesterday = [];
    const week = [];
    const month = [];
    const older = new Map(); // key → { label, items[] }

    decoys.forEach((n, i) => {
      // Pinned rows live in their own section above every date group,
      // ordered by their storage index (the order in which they were
      // pinned). They never appear inside a date bucket — matches iOS
      // Notes exactly.
      if (n.pinned) {
        pinned.push({ n, i });
        return;
      }
      const d = inferredDates[i] || now;
      const dayStart = new Date(d);
      dayStart.setHours(0, 0, 0, 0);
      const diffDays = Math.floor((startOfToday.getTime() - dayStart.getTime()) / (24 * 60 * 60 * 1000));
      const entry = { n, i };
      if (diffDays <= 0) today.push(entry);
      else if (diffDays === 1) yesterday.push(entry);
      else if (diffDays < 8) week.push(entry);
      else if (diffDays < 31) month.push(entry);
      else {
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (!older.has(key)) {
          const label = capitalize(
            d.toLocaleDateString(locale, { month: "long", year: "numeric" })
          );
          older.set(key, { label, items: [] });
        }
        older.get(key).items.push(entry);
      }
    });

    // Inject the session-persisted prediction (if any) into the same
    // date-buckets so it looks like just another organic decoy in the
    // list. Uses its own dateISO to pick the correct bucket; falls
    // through into "today" for malformed ISO strings.
    //
    // Gated on `searchForceArmed`: the prediction row must NOT surface
    // until the magician has tapped the camera quick-action on the
    // fake lockscreen (which is the sole call site that sets
    // searchForceArmed AND clears any stale persisted snapshot from a
    // previous trick). Without this gate an old persisted note from
    // last night's show would randomly sit on top of the Notes list
    // the moment a curious spectator opened the app — a real visual
    // "tell" that the app is more than it appears.
    if (searchForceArmed && searchForcePersisted && searchForcePersisted.body) {
      const parsed = new Date(searchForcePersisted.dateISO || "");
      const pd = Number.isFinite(parsed.getTime()) ? parsed : now;
      const pdStart = new Date(pd);
      pdStart.setHours(0, 0, 0, 0);
      const pDiff = Math.floor((startOfToday.getTime() - pdStart.getTime()) / (24 * 60 * 60 * 1000));
      const firstLine = (searchForcePersisted.body.split("\n").find((l) => l.trim().length > 0) || "").trim();
      const persistedNote = {
        id: "__search_force_persisted__",
        title: searchForcePersisted.title || searchForceConfig.title || "",
        preview: firstLine.slice(0, 80),
        body: searchForcePersisted.body,
        isSearchForce: true,
        isSearchForcePersisted: true,
        // Show the localized full-date label (e.g. "14 apr om 10:31")
        // as the note's header date when the spectator taps back into
        // it. Falls back to legacy free-form dateLabel string.
        dateLabel:
          formatSearchForceNoteDate(pd, locale, langCode)
          || searchForceConfig.dateLabel,
        // Real timestamp for the row's right-side time display.
        _persistedDate: pd,
      };
      // Persisted entries use i:-1 so nothing in the swipe/edit code
      // paths tries to index into the decoys array with a bad key.
      const persistedEntry = { n: persistedNote, i: -1, isSearchForce: true, isSearchForcePersisted: true };
      if (pDiff <= 0) today.unshift(persistedEntry);
      else if (pDiff === 1) yesterday.unshift(persistedEntry);
      else if (pDiff < 8) week.unshift(persistedEntry);
      else if (pDiff < 31) month.unshift(persistedEntry);
      else {
        const key = `${pd.getFullYear()}-${pd.getMonth()}`;
        if (!older.has(key)) {
          const label = capitalize(
            pd.toLocaleDateString(locale, { month: "long", year: "numeric" })
          );
          older.set(key, { label, items: [] });
        }
        older.get(key).items.unshift(persistedEntry);
      }
    }

    const groups = [];
    if (pinned.length)    groups.push({ id: "pinned",    label: t("pinned", langCode),                 items: pinned, isPinned: true });
    if (today.length)     groups.push({ id: "today",     label: t("group_today", langCode),            items: today });
    if (yesterday.length) groups.push({ id: "yesterday", label: t("group_yesterday", langCode),        items: yesterday });
    if (week.length)      groups.push({ id: "week",      label: t("group_previous_7_days", langCode),  items: week });
    if (month.length)     groups.push({ id: "month",     label: t("group_previous_30_days", langCode), items: month });
    // Older buckets sorted newest-first.
    const olderKeys = Array.from(older.keys()).sort((a, b) => {
      const [ay, am] = a.split("-").map(Number);
      const [by, bm] = b.split("-").map(Number);
      return by !== ay ? by - ay : bm - am;
    });
    olderKeys.forEach((k, idx) => {
      const o = older.get(k);
      groups.push({ id: `older-${idx}`, label: o.label, items: o.items });
    });
    return groups;
  }, [decoys, inferredDates, locale, langCode, searchQuery, searchForceConfig, searchForceArmed, searchForceAnswers, searchForcePersisted]);

  // Acrostic override map — keys are original `decoys[i]` indices, values
  // are `{ title, preview }` to render in place of the raw values.
  //
  // Placement policy (per user spec, iter_57):
  //   • Runs repeat up to 3× evenly spread across the visible list
  //     (~1/4, ~1/2, ~3/4 marks) so the magician can find one within
  //     one screen of scrolling even on a 150+ note deck.
  //   • Position 0 stays authentic (top of list unchanged from what
  //     the spectator glanced at seconds before).
  //   • Different seed per run so the three copies read as distinct
  //     mundane notes rather than three carbon copies stacked.
  //   • Titles come from the language-specific bank matching the
  //     current Settings `LANGUAGE` (falls back to English for
  //     non-Latin-script languages).
  //   • Hidden while search is active — search results must never
  //     leak the acrostic titles, otherwise typing "kids" on the
  //     spectator's phone would surface a suspiciously perfect match.
  //   • Bail out entirely when the card can't be encoded (unknown
  //     value/suit, or an armed Joker whose value word contains a
  //     letter we have no bank entry for).
  const acrosticOverridesByIdx = useMemo(() => {
    if (!acrosticCard || !isAcrosticEnabled()) return {};
    if (isSearchActive) return {};
    // Flatten the visible-order list of {n, i} entries, skipping any
    // "search" or "pinned" pseudo-groups AND any row that already
    // carries a trick role (search-force, tenouts, swiper, apocalypse
    // seed etc.). Trick rows have their own title logic — we would
    // both break their reveal AND create a gap in the acrostic run if
    // we tried to include them.
    const flat = [];
    for (const grp of groupedDecoys) {
      if (grp.isSearch) continue;
      if (grp.id === "pinned") continue;
      for (const item of grp.items || []) {
        if (!item || typeof item.i !== "number") continue;
        if (roleByIndex[item.i]) continue;
        flat.push(item);
      }
    }
    const withOverrides = applyAcrosticOverrides(
      flat.map((entry) => ({ id: entry.n.id, title: entry.n.title, preview: entry.n.preview })),
      acrosticCard,
      langCode,
    );
    const map = {};
    withOverrides.forEach((over, pos) => {
      if (!over || !over.__acrostic) return;
      const origIdx = flat[pos]?.i;
      if (typeof origIdx !== "number") return;
      map[origIdx] = { title: over.title, preview: over.preview };
    });
    return map;
  }, [acrosticCard, groupedDecoys, isSearchActive, roleByIndex, langCode]);

  // Triple-tap on the top "Folders" header → quiet exit to Setup.
  // Single tap → navigate to the real Folders view (the iOS-native
  // behaviour a curious spectator would expect). The single-tap
  // navigation is deferred by ~350 ms so a rapid triple can preempt it
  // and route to /setup instead — that keeps the magician's escape
  // hatch fully intact while restoring realistic navigation.
  // Also bound at the window level on the top 60 px strip so the
  // magician can bail from anywhere along the iOS-status-bar zone —
  // belt-and-suspenders escape hatch during the haunt/emptied beats
  // where overlays might otherwise sit above a button. The
  // window-level useEffect lives FURTHER DOWN (right after the
  // `clearApocalypseAnimationState` useCallback) to dodge the TDZ
  // that caught us in iter_20.
  const taps = useMemo(() => ({ list: [], topList: [] }), []);
  const headerNavTimer = useRef(null);
  const onHeaderTap = () => {
    const now = Date.now();
    taps.list = [...taps.list, now].filter((t) => now - t < 700);
    if (taps.list.length >= 3) {
      // Magician escape wins — cancel the pending single-tap nav.
      taps.list = [];
      if (headerNavTimer.current) {
        clearTimeout(headerNavTimer.current);
        headerNavTimer.current = null;
      }
      // Bailing to Setup also resets any in-flight apocalypse so the
      // next session starts from clean titles.
      clearApocalypseAnimationState();
      clearApocalypseArmed();
      // Wipe the transient Swiper card too. Per user request the
      // Swiper stays armed across all in-notes navigation (open/close
      // trick, back to /folders, etc.) — the only place it resets is
      // this triple-tap escape back to Setup, signalling the end of
      // the performance.
      clearSwiperCard();
      // Same reasoning — acrostic override lives for the duration of
      // one card reveal. Escaping back to Setup clears it so the next
      // performance starts on truly-random decoy titles.
      clearAcrostic();
      // Reset the Flip Force 2-stage cursor so the next session starts
      // fresh at Force Note 1. This is the ONLY way the cursor resets.
      resetFlipForceStage();
      navigate("/");
      return;
    }
    // Debounced single-tap → /folders. If a follow-up tap arrives
    // within the 700 ms window it will either add to the triple-tap
    // sequence (canceling this timer above) or extend the debounce.
    if (headerNavTimer.current) clearTimeout(headerNavTimer.current);
    headerNavTimer.current = setTimeout(() => {
      headerNavTimer.current = null;
      taps.list = [];
      navigate("/folders");
    }, 350);
  };

  // --- Swiper gesture (in-place, silent) ---------------------------------
  // Two encodings share the same touch handler. The mode is decided in
  // Settings and read once at mount.
  //   • "cards"  — 3-swipe playing-card encoding (52 cards + Joker)
  //   • "custom" — magician-defined combo list. Each combo is 1–3 arrows
  //               mapped to a word; the matching word lands in (xxx).
  // Custom-mode uses a pause-to-finalize window so multi-swipe combos
  // have time to land before short single-arrow combos preempt them.
  // Nothing visible happens on screen — the captured value lands in
  // localStorage and is picked up by /trick when the top decoy is tapped.
  const swipeBuf = useRef([]);
  const customTimer = useRef(null);
  const touch = useRef({ x: 0, y: 0, t: 0 });
  const lastFlickAt = useRef(0);

  // Pre-compute the magician's combo lookup so the hot path runs in O(1).
  const customCombos = swiperConfig.combos || [];
  const customComboMap = useMemo(() => {
    const m = new Map();
    customCombos.forEach((c) => {
      if (c.dirs && c.word) m.set(c.dirs, c.word);
    });
    return m;
  }, [customCombos]);
  const customMaxLen = useMemo(
    () => customCombos.reduce((mx, c) => Math.max(mx, (c.dirs || "").length), 1),
    [customCombos]
  );

  /** Look up the current buffer in the combo map. If an exact match
   *  exists AND no longer combo could still match (i.e. no combo has
   *  the current buffer as a strict prefix), fire immediately. */
  const finalizeCustomCombo = useCallback(() => {
    if (customTimer.current) {
      clearTimeout(customTimer.current);
      customTimer.current = null;
    }
    const key = swipeBuf.current.join("");
    swipeBuf.current = [];
    if (!key) return;
    const word = customComboMap.get(key);
    if (word) {
      setSwiperCustomWord(word);
      // Successful custom-combo capture → auto-release the "…" lock so
      // normal scrolling resumes immediately (per magic-flow spec).
      setSwiperLocked(false);
    }
  }, [customComboMap]);

  const onTouchStart = (e) => {
    if (!swiperConfig.enabled) return;
    if (!swiperLocked) return; // Swiper only listens while the "…" lock is engaged.
    const p = e.touches[0];
    touch.current = { x: p.clientX, y: p.clientY, t: Date.now() };
  };

  const onTouchEnd = (e) => {
    if (!swiperConfig.enabled) return;
    if (!swiperLocked) return; // Swiper only listens while the "…" lock is engaged.
    const ended = e.changedTouches[0];
    const dx = ended.clientX - touch.current.x;
    const dy = ended.clientY - touch.current.y;
    const dt = Date.now() - touch.current.t;
    const dist = Math.hypot(dx, dy);

    if (dist < MIN_DIST || dt > MAX_DURATION) return;       // too short / slow → ignore
    if (Date.now() - lastFlickAt.current < IGNORE_AFTER_MS) return; // debounce

    const ax = Math.abs(dx), ay = Math.abs(dy);
    let dir;
    if (ax > ay * DOMINANCE)       dir = dx > 0 ? "R" : "L";
    else if (ay > ax * DOMINANCE)  dir = dy > 0 ? "D" : "U";
    else return;                                            // ambiguous diagonal → ignore

    lastFlickAt.current = Date.now();

    // --- Custom-combo mode -----------------------------------------------
    // Append, then decide:
    //   • exact match AND no longer combo starts with this buffer → fire now
    //   • some longer combo starts with this buffer            → wait (timer)
    //   • no match and no possible future match                 → reset
    if (swiperConfig.mode === "custom") {
      if (customTimer.current) {
        clearTimeout(customTimer.current);
        customTimer.current = null;
      }
      swipeBuf.current.push(dir);
      const key = swipeBuf.current.join("");
      const hasExact = customComboMap.has(key);
      const hasFuture = key.length < customMaxLen &&
        customCombos.some((c) => c.dirs && c.dirs.startsWith(key) && c.dirs.length > key.length);

      if (hasExact && !hasFuture) {
        finalizeCustomCombo();
        return;
      }
      if (!hasExact && !hasFuture) {
        swipeBuf.current = [];
        return;
      }
      if (swipeBuf.current.length >= customMaxLen) {
        finalizeCustomCombo();
        return;
      }
      // Schedule finalize so multi-swipe combos commit when the magician
      // pauses; the window also covers the case where a longer combo
      // exists but the user only meant the shorter prefix.
      customTimer.current = setTimeout(() => finalizeCustomCombo(), 700);
      return;
    }

    // --- Cards mode: existing 3-swipe encoding ---
    swipeBuf.current.push(dir);

    // After two swipes, check for Joker (R+L) — locks in early, no suit.
    if (swipeBuf.current.length === 2) {
      const v = decodeClockValue(swipeBuf.current);
      if (v === "JOKER") {
        setSwiperCard({ value: "JOKER", suit: null });
        // Auto-release the "…" lock so normal scrolling resumes.
        setSwiperLocked(false);
        swipeBuf.current = [];
        return;
      }
      if (v === null) {
        swipeBuf.current = [];
        return;
      }
    }

    if (swipeBuf.current.length === 3) {
      const card = decodeClockCard(swipeBuf.current);
      if (card && card.value) {
        setSwiperCard(card);
        // Auto-release the "…" lock so normal scrolling resumes.
        setSwiperLocked(false);
      }
      swipeBuf.current = [];
    }
  };

  // Wipe any pending custom-combo timer when the page goes away.
  useEffect(() => () => {
    if (customTimer.current) clearTimeout(customTimer.current);
  }, []);

  // --- Apocalypse trigger -------------------------------------------------
  // Helper to wipe any in-flight glitch + reset all phase state. Called
  // when the magician re-arms (heads to /lock), exits to Setup, or the
  // spectator shakes the phone to dismiss the effect.
  // Full cold-reset: clears EVERY apocalypse-related piece of state and
  // tears down the wave detector + reset overlay. Used on unmount,
  // on triple-tap exit, and after the post-shake reset completes.
  const clearApocalypseAnimationState = useCallback(() => {
    clearApocalypseTimers();
    setApocalypseTitles([]);
    setApocalypseExtras([]);
    setWipeTitles([]);
    setApocalypseWiped(false);
    setApocalypseCounterOverride(null);
    setShowCueDot(false);
    setApocalypsePhase("idle");
    setFlashOn(false);
    setPushBanners([]);
    setShowErrorDialog(false);
    reGlitchUsedRef.current = false;
    setRestoreFlashTitle("");
  }, []);

  // Window-level triple-tap escape hatch — bound on the top 60 px
  // strip so the magician can bail to /setup from anywhere along the
  // iOS-status-bar zone, even if a stray overlay would otherwise sit
  // above the back button. Lives here (after the useCallback above)
  // to dodge the TDZ that bit us in iter_20.
  useEffect(() => {
    const handler = (e) => {
      const y = e.touches?.[0]?.clientY ?? e.clientY;
      if (typeof y !== "number" || y > 60) return;
      const now = Date.now();
      taps.topList = [...taps.topList, now].filter((t) => now - t < 700);
      if (taps.topList.length >= 3) {
        taps.topList = [];
        clearApocalypseAnimationState();
        clearApocalypseArmed();
        // Clear the one-shot rehearsal flag so the next manual run
        // doesn't auto-redirect to /settings.
        consumeApocalypseRehearsing();
        // Swiper resets only on this escape — see the matching call
        // in onHeaderTap. Mirrors the "end of performance" semantics.
        clearSwiperCard();
        // Reset the Flip Force 2-stage cursor — see onHeaderTap above.
        resetFlipForceStage();
        navigate("/");
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [clearApocalypseAnimationState, navigate, taps]);

  // Post-shake reset — drives one of two presets configured in
  // Settings → Apocalypse:
  //   • "instant" — immediately drop back to the clean decoy folder
  //   • "flash"   — hold the empty beat for `flashDelayMs`, then fire
  //                  N short white camera-pop flashes (`flashCount`,
  //                  `flashDurationMs` on, `flashGapMs` off) and only
  //                  THEN reset.
  // The previous fake-iOS reboot was retired — iOS doesn't let a PWA
  // hide the system clock/battery, which kept giving the trick away.
  const runResetSequence = useCallback(() => {
    const preset = apocalypseCfg.resetPreset;
    // Whatever preset runs, restore the OS status-bar in case FX#4
    // (theme-color cycle) was still active.
    if (themeStopRef.current) {
      try { themeStopRef.current(); } catch { /* ignore */ }
      themeStopRef.current = null;
    }

    // Capture the card that drove the most recent reveal BEFORE the
    // finish step clears it. We paint the second-prediction sentence
    // into every preset decoy title for the post-restore "1-second
    // flash" beat.
    //   • one-shake mode → the lockscreen-input (PIN-armed) card
    //   • two-shake mode → the spectator's SECOND clock-swipe card if
    //                      they encoded one during the error dialog
    //                      (falls back to the armed card otherwise)
    const armed = readApocalypseArmed();
    const swiped =
      apocalypseCfg.shakeMode === "two-shake" ? readSwiperCard() : null;
    const flashCard =
      swiped && swiped.value
        ? { value: swiped.value, suit: swiped.suit }
        : armed
          ? { value: armed.value, suit: armed.suit }
          : null;
    const restoreSentence =
      apocalypseCfg.restoreFlashEnabled && flashCard
        ? formatApocalypseCard(
            flashCard,
            apocalypseCfg.secondTemplate,
            langCode
          )
        : "";

    const finish = () => {
      clearApocalypseAnimationState();
      clearApocalypseArmed();
      // Belt-and-braces: also wipe the transient swiper card so the
      // next trick session starts from a clean slate.
      clearSwiperCard();
      // Post-restore 1-second flash — paint the SECOND prediction
      // sentence into every preset decoy title for `restoreFlashMs`,
      // then drop the override so the clean Notes folder returns.
      const wasRehearsal = consumeApocalypseRehearsing();
      if (restoreSentence && apocalypseCfg.restoreFlashMs > 0) {
        setRestoreFlashTitle(restoreSentence);
        const id = setTimeout(() => {
          setRestoreFlashTitle("");
          if (wasRehearsal) navigate("/settings");
        }, apocalypseCfg.restoreFlashMs);
        apocalypseTimers.current.push(id);
      } else if (wasRehearsal) {
        navigate("/settings");
      }
    };

    if (preset === "instant") {
      finish();
      return;
    }

    // "flash" preset: hold empty beat → blink N times → reset.
    const delay = Math.max(0, apocalypseCfg.flashDelayMs);
    const dur = Math.max(50, apocalypseCfg.flashDurationMs);
    const gap = Math.max(50, apocalypseCfg.flashGapMs);
    const count = Math.max(1, Math.min(3, apocalypseCfg.flashCount));
    let t = delay;
    for (let i = 0; i < count; i++) {
      apocalypseTimers.current.push(setTimeout(() => setFlashOn(true), t));
      apocalypseTimers.current.push(setTimeout(() => setFlashOn(false), t + dur));
      t += dur + gap;
    }
    // Final reset fires just after the last flash ends.
    apocalypseTimers.current.push(setTimeout(finish, t));
  }, [
    apocalypseCfg.resetPreset,
    apocalypseCfg.flashDelayMs,
    apocalypseCfg.flashDurationMs,
    apocalypseCfg.flashGapMs,
    apocalypseCfg.flashCount,
    apocalypseCfg.restoreFlashEnabled,
    apocalypseCfg.restoreFlashMs,
    apocalypseCfg.secondTemplate,
    apocalypseCfg.shakeMode,
    clearApocalypseAnimationState,
    langCode,
    navigate,
  ]);

  // Shake-to-empty: spectator shakes the phone, the notes (decoys +
  // extras) vanish, the screen looks like a brand-new Notes folder.
  // The glitch animations STOP at this point — the empty list is the
  // "broken phone" beat. From here the configured reset preset
  // (instant or flash) decides how we restore the clean Notes list.
  const enterEmptiedAfterShake = useCallback(() => {
    clearApocalypseTimers();
    // FX teardown so the status bar / speech don't keep firing after
    // the "phone fixes itself" moment.
    if (themeStopRef.current) {
      try { themeStopRef.current(); } catch { /* ignore */ }
      themeStopRef.current = null;
    }
    cancelSpeechFx();
    setApocalypseExtras([]);
    setApocalypseTitles([]);
    setWipeTitles([]);
    setApocalypseWiped(true);
    setApocalypseCounterOverride(null);
    setShowCueDot(false);
    setPushBanners([]);
    setShowErrorDialog(false);
    setApocalypsePhase("emptied");
    // Hand off to the configured reset preset.
    runResetSequence();
  }, [runResetSequence]);

  // Refs to break the circular dependency between triggerReGlitch (which
  // needs to re-arm the shake listener after the re-glitch beat) and
  // startApocalypseShake (which is declared further down and would TDZ
  // if referenced from triggerReGlitch's useCallback deps).
  // Ref mirror of apocalypseExtras length so triggerReGlitch can read
  // the row count WITHOUT taking a state dependency — otherwise the
  // useCallback identity would shift every spawn-phase tick, cascading
  // through onShakeFired → startApocalypseShake → runApocalypse and
  // re-triggering the entire animation from wipe on every extras tick.
  const apocalypseExtrasLenRef = useRef(0);
  useEffect(() => {
    apocalypseExtrasLenRef.current = apocalypseExtras.length;
  }, [apocalypseExtras.length]);

  const startApocalypseShakeRef = useRef(null);

  // Pass-4: extracted resolve-row loop. Schedules a per-row scramble
  // that locks into `sentence`, starting at `baseAt` (absolute setTimeout
  // delay from now), and fires `onAllDone` once the last row settles.
  // Used by both the initial runApocalypse (start of phase 3) and the
  // two-shake re-glitch beat (after the spectator's second card has
  // been clock-swiped during the "error" dialog).
  const scheduleResolveLoop = useCallback(
    (sentence, baseAt, totalRows, opts = {}) => {
      const {
        rowStagger = apocalypseCfg.rowStagger,
        rowFrame = apocalypseCfg.rowFrame,
        rowCycle = apocalypseCfg.rowCycle,
        onAllDone = null,
      } = opts;
      for (let i = 0; i < totalRows; i++) {
        const start = baseAt + i * rowStagger;
        apocalypseTimers.current.push(
          setTimeout(() => {
            const list = listScrollRef.current;
            if (list) {
              const row = list.querySelector(`[data-row-index="${i}"]`);
              if (row) {
                animateScrollToRow(list, row, apocalypseCfg.scrollSpeedMs);
              }
            }
            const cycleStart = Date.now();
            const tick = () => {
              const elapsed = Date.now() - cycleStart;
              if (elapsed >= rowCycle) {
                setApocalypseTitles((arr) => {
                  const next = arr.slice();
                  next[i] = sentence;
                  return next;
                });
                return;
              }
              const lockedLen = Math.floor((elapsed / rowCycle) * sentence.length);
              const locked = sentence.slice(0, lockedLen);
              const scrambleLen = Math.max(0, sentence.length - lockedLen);
              const scramble = randomGlitchString(scrambleLen);
              setApocalypseTitles((arr) => {
                const next = arr.slice();
                next[i] = locked + scramble;
                return next;
              });
              const id = setTimeout(tick, rowFrame);
              apocalypseTimers.current.push(id);
            };
            tick();
          }, start)
        );
      }
      if (onAllDone) {
        const total = baseAt + totalRows * rowStagger + rowCycle;
        apocalypseTimers.current.push(setTimeout(onAllDone, total));
      }
    },
    [
      apocalypseCfg.rowStagger,
      apocalypseCfg.rowFrame,
      apocalypseCfg.rowCycle,
      apocalypseCfg.scrollSpeedMs,
    ]
  );

  // Pass-4: re-glitch beat. Fired on the FIRST shake in two-shake mode
  // while the error dialog is up. Reads the spectator's freshly
  // clock-swiped second card (falls back to the original armed card if
  // they didn't encode one), rebuilds the sentence using the
  // second-prediction template, and re-runs the resolve loop on the
  // already-spawned rows. Once complete, scrolls back to the top and
  // re-raises the iOS-style error dialog so the SECOND shake performs
  // the clean reset (handler already routes to enterEmptiedAfterShake
  // because reGlitchUsedRef is now true).
  const triggerReGlitch = useCallback(() => {
    const armed = readApocalypseArmed();
    if (!armed) {
      // No armed card to fall back to — just clean reset.
      enterEmptiedAfterShake();
      return;
    }
    // Prefer the magician's most-recent clock-swipe; fall back to the
    // PIN-encoded armed card if they didn't swipe a second one.
    const swiped = readSwiperCard();
    const card = swiped && swiped.value
      ? { value: swiped.value, suit: swiped.suit }
      : { value: armed.value, suit: armed.suit };
    const sentence = formatApocalypseCard(
      card,
      apocalypseCfg.secondTemplate,
      langCode
    );
    if (!sentence) {
      enterEmptiedAfterShake();
      return;
    }
    // Tear down the prior haunt timers so they don't fight the new loop.
    clearApocalypseTimers();
    setShowErrorDialog(false);
    setApocalypsePhase("reGlitch");
    reGlitchUsedRef.current = true;
    const totalRows = apocalypseExtrasLenRef.current;
    scheduleResolveLoop(sentence, 0, totalRows, {
      onAllDone: () => {
        // Realism: freeze in-place instead of smooth-scrolling to the
        // top before re-raising the dialog. The second reveal note is
        // rendered at row 1 anyway, so if the magician handed the
        // phone back scrolled a couple of rows down, the shake-freeze
        // reads as an authentic hard-lock rather than a scripted jump.
        // Re-raise the iOS-style error dialog — exact same lock as the
        // first reveal. The shake handler will now route to clean
        // reset because reGlitchUsedRef.current is true.
        if (apocalypseCfg.errorEnabled) {
          // Same freeze semantics as the first raise: kill any
          // theme-color flicker so the status bar also stops moving.
          if (themeStopRef.current) {
            try { themeStopRef.current(); } catch { /* ignore */ }
            themeStopRef.current = null;
          }
          setApocalypsePhase("error");
          setShowErrorDialog(true);
          try {
            playErrorTritone({
              volume: (apocalypseCfg.errorSoundVolume ?? 80) / 100,
            });
          } catch { /* ignore */ }
        } else {
          setApocalypsePhase("haunt");
        }
        // Re-arm the shake listener with a 600 ms grace period so the
        // spectator's still-decaying hand motion can't fire a second
        // shake immediately and skip the freshly-raised error dialog.
        // Without this, on real devices the wave detector catches the
        // tail of the same shake the magician just performed.
        apocalypseTimers.current.push(
          setTimeout(() => {
            startApocalypseShakeRef.current?.();
          }, 600)
        );
      },
    });
  }, [
    apocalypseCfg.errorEnabled,
    apocalypseCfg.errorSoundVolume,
    apocalypseCfg.secondTemplate,
    enterEmptiedAfterShake,
    langCode,
    scheduleResolveLoop,
  ]);

  // Pass-4: shake-handler dispatch. Picks between re-glitch (two-shake
  // mode, first shake during the error dialog) and the clean reset.
  const onShakeFired = useCallback(() => {
    const phase = apocalypsePhaseRef.current;
    if (
      phase === "error" &&
      apocalypseCfg.shakeMode === "two-shake" &&
      !reGlitchUsedRef.current
    ) {
      triggerReGlitch();
      return;
    }
    enterEmptiedAfterShake();
  }, [
    apocalypseCfg.shakeMode,
    enterEmptiedAfterShake,
    triggerReGlitch,
  ]);
  const stopApocalypseShake = useCallback(() => {
    const s = apocalypseShake.current;
    if (s.handler) {
      window.removeEventListener("devicemotion", s.handler);
      s.handler = null;
    }
    if (s.windowTimer) {
      clearTimeout(s.windowTimer);
      s.windowTimer = null;
    }
    s.lastMag = 0;
    s.peakCount = 0;
  }, []);

  // Listen on the accelerometer for a sustained shake. When detected,
  // wipe the apocalypse so the phone snaps back to the clean decoy
  // folder. iOS Safari requires DeviceMotionEvent.requestPermission()
  // to be called from a user gesture — the magician pre-grants that
  // from Settings before performing. If permission is denied or the
  // sensor is unavailable, the trick still works; the spectator just
  // can't dismiss it (the magician can long-press the counter and
  // navigate back through Setup).
  const startApocalypseShake = useCallback(() => {
    if (apocalypseShake.current.handler) return; // already running
    if (typeof window === "undefined" || !("DeviceMotionEvent" in window)) return;
    const THRESHOLD = apocalypseCfg.shakeThreshold;
    const NEEDED = apocalypseCfg.shakeCount;
    const WINDOW_MS = 1000;

    const handler = (ev) => {
      // Prefer `acceleration` (gravity removed → idle baseline ≈ 0).
      // Fall back to `accelerationIncludingGravity` for devices that
      // don't expose the former; rolling delta cancels the baseline.
      const a =
        ev.acceleration && (ev.acceleration.x != null || ev.acceleration.y != null || ev.acceleration.z != null)
          ? ev.acceleration
          : ev.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.sqrt(
        (a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2
      );
      const s = apocalypseShake.current;
      const delta = Math.abs(mag - s.lastMag);
      s.lastMag = mag;
      if (delta <= THRESHOLD) return;
      s.peakCount += 1;
      if (s.windowTimer) clearTimeout(s.windowTimer);
      s.windowTimer = setTimeout(() => {
        s.peakCount = 0;
        s.windowTimer = null;
      }, WINDOW_MS);
      if (s.peakCount >= NEEDED) {
        stopApocalypseShake();
        onShakeFired();
      }
    };
    apocalypseShake.current.handler = handler;
    window.addEventListener("devicemotion", handler);
  }, [
    apocalypseCfg.shakeThreshold,
    apocalypseCfg.shakeCount,
    onShakeFired,
    stopApocalypseShake,
  ]);

  // Keep the re-glitch ref pointing at the latest startApocalypseShake
  // callback so triggerReGlitch can re-arm the listener without taking
  // a circular dependency.
  useEffect(() => {
    startApocalypseShakeRef.current = startApocalypseShake;
  }, [startApocalypseShake]);

  // On mount (and whenever the decoy list changes) check whether the
  // magician armed the trick on /lock. If so, schedule the multi-phase
  // animation. Re-runs on visibility-change too so if the magician
  // backgrounds the app and returns mid-window, the remaining delay
  // still fires.
  const runApocalypse = useCallback(() => {
    const armed = readApocalypseArmed();
    if (!armed) return;
    // Stale-arm guard: drop anything armed more than 15 minutes ago so a
    // forgotten session doesn't ambush the magician on next app launch.
    const STALE_MS = 15 * 60 * 1000;
    if (armed.armedAt && Date.now() - armed.armedAt > STALE_MS) {
      clearApocalypseArmed();
      return;
    }
    const sentence = formatApocalypseCard(
      { value: armed.value, suit: armed.suit },
      apocalypseCfg.template,
      langCode
    );
    if (!sentence) return;
    // Bare card label ("four of spades") used by FX templates that
    // want to interpolate just the card, not the full forced sentence.
    const cardLabel = apocalypseCardLabel(
      { value: armed.value, suit: armed.suit },
      langCode
    );
    const fillTemplate = (tmpl) =>
      (tmpl || "").replace(/\(xxx\)/gi, cardLabel).replace(/\{card\}/gi, cardLabel);
    const remaining = Math.max(
      0,
      (armed.armedAt || 0) + (armed.delay || 0) * 1000 - Date.now()
    );
    clearApocalypseTimers();

    // All timing knobs come from the magician's Settings.
    const WIPE_FRAME_MS = apocalypseCfg.wipeFrameMs;
    const POST_WIPE_DELAY_MS = apocalypseCfg.postWipeDelayMs;
    const PHASE_SPAWN_GAP = apocalypseCfg.extrasInterval;
    const EXTRA_COUNT = apocalypseCfg.extrasCount;
    const PHASE_FLICKER_MS = apocalypseCfg.flickerMs;
    const ROW_STAGGER_MS = apocalypseCfg.rowStagger;
    const ROW_FRAME_MS = apocalypseCfg.rowFrame;
    const ROW_CYCLE_MS = apocalypseCfg.rowCycle;

    const extraTitles = pickApocalypseExtras(
      EXTRA_COUNT,
      apocalypseCfg.extrasTitles
    );

    // ---------- Phase 0: reverse-hack wipe of every preset title ------
    // We seed `wipeTitles` with the full preset titles, then tick once
    // per WIPE_FRAME_MS dropping the last character of every row
    // simultaneously. When all rows are empty, flip `apocalypseWiped`
    // so the preset list vanishes from the render entirely.
    const presetTitles = decoys.map((n) => n.title || "");
    const maxLen = presetTitles.reduce((m, s) => Math.max(m, s.length), 0);
    const wipeStart = remaining;
    // Pre-wipe cue dot: light up `cueDot.leadSec` seconds before the
    // wipe (or immediately if the configured delay is shorter), and
    // snap off when the wipe begins. Magician glances at it to know
    // exactly when to look up. All visual knobs (position/size/
    // color/opacity/lead) live on `apocalypseCfg.cueDot` so the
    // magician can tune the indicator from Settings.
    const cueLeadMs = (apocalypseCfg.cueDot?.leadSec ?? 3) * 1000;
    const cueOnAt = Math.max(0, wipeStart - cueLeadMs);
    apocalypseTimers.current.push(
      setTimeout(() => setShowCueDot(true), cueOnAt)
    );
    apocalypseTimers.current.push(
      setTimeout(() => setShowCueDot(false), wipeStart)
    );
    apocalypseTimers.current.push(
      setTimeout(() => {
        setApocalypsePhase("wipe");
        // Cellular interference bed — plays underneath the ENTIRE crash
        // sequence (wipe → spawn → scramble → resolve) and cuts dead the
        // moment phaseHauntStart fires the error dialog. Uses the DOM
        // <audio ref> element (see JSX) for absolute iOS Safari
        // reliability — no Web Audio context state weirdness. Volume
        // tracks the magician's Apocalypse sound-volume slider.
        try {
          const iEl = interferenceRef.current;
          if (iEl && apocalypseCfg.soundEnabled && apocalypseCfg.soundVolume > 0) {
            iEl.volume = Math.max(0, Math.min(1, apocalypseCfg.soundVolume / 100));
            try { iEl.currentTime = 0; } catch { /* pre-load ok */ }
            const p = iEl.play();
            if (p && typeof p.catch === "function") {
              // Autoplay-policy rejection retry: on iOS Safari, the first
              // .play() from a timer (not a gesture) can reject IF no
              // gesture audio has fired yet. The banner pings that
              // follow will bootstrap the audio session; a 60 ms retry
              // catches the window right after.
              p.catch(() => {
                setTimeout(() => {
                  try { iEl.play(); } catch { /* ignore */ }
                }, 60);
              });
            }
          }
        } catch { /* ignore */ }
        // Vibration bed — kicks in alongside the interference. Same
        // autoplay-policy retry loop as the interference play() so the
        // iOS Safari cold-start case doesn't drop the buzz. Volume is
        // read from the magician's vibration slider (0..100), but we
        // deliberately START at 40% of that and RAMP to 100% over the
        // entire haunt duration so the buzz feels like the phone is
        // "trying harder and harder" to vibrate the closer it gets to
        // dying. The audio-only build-up carries the dramatic weight
        // that a real iOS device can't (Vibration API is blocked on
        // Safari) — a linear ramp reads as inevitable, unstoppable.
        try {
          const vEl = vibrationRef.current;
          if (vEl && apocalypseCfg.vibrationEnabled && apocalypseCfg.vibrationVolume > 0) {
            const baseVol = Math.max(0, Math.min(1, apocalypseCfg.vibrationVolume / 100));
            const startVol = baseVol * 0.4;
            vEl.volume = startVol;
            try { vEl.currentTime = 0; } catch { /* pre-load ok */ }
            const p = vEl.play();
            if (p && typeof p.catch === "function") {
              p.catch(() => {
                setTimeout(() => {
                  try { vEl.play(); } catch { /* ignore */ }
                }, 60);
              });
            }
            // Kick off the volume ramp. Duration = time from now (wipe
            // fire) until the error dialog would raise. Read from the
            // outer-scope const `phaseHauntStart` which is declared
            // AFTER this setTimeout is registered but INITIALISED
            // before this callback fires (temporal-dead-zone is fine
            // because the callback runs asynchronously).
            const rampDurationMs = Math.max(400, phaseHauntStart - wipeStart);
            const STEPS = 40;
            const stepMs = rampDurationMs / STEPS;
            let stepIdx = 0;
            const rampId = setInterval(() => {
              stepIdx += 1;
              const t = Math.min(1, stepIdx / STEPS);
              // Slight ease-in (t^1.4) — the last third of the ramp
              // ramps up faster than the first two thirds, so the
              // build-up peaks right before the error hits. Feels
              // more "climactic" than a flat linear ramp.
              const eased = Math.pow(t, 1.4);
              try {
                vEl.volume = Math.min(1, startVol + (baseVol - startVol) * eased);
              } catch { /* ignore */ }
              if (stepIdx >= STEPS) clearInterval(rampId);
            }, stepMs);
            // clearTimeout also clears intervals in browsers, so this
            // is safe to push onto the same cancellation list used
            // for every other apocalypse timer.
            apocalypseTimers.current.push(rampId);
          }
        } catch { /* ignore */ }
        // FX #4 — Theme-color status-bar flicker. Kicked off the MOMENT
        // the wipe starts so the browser chrome (iOS Safari address bar
        // tint) begins strobing in sync with the decoy titles glitching
        // out. Was previously gated to phase2Start (post-spawn); moving
        // it here makes the whole "phone crashing" beat feel unified —
        // one continuous crash from the first glitched character.
        if (apocalypseCfg.fxThemeEnabled) {
          if (themeStopRef.current) {
            try { themeStopRef.current(); } catch { /* ignore */ }
          }
          themeStopRef.current = startThemeColorFlickerFx(
            apocalypseCfg.fxThemeColors,
            apocalypseCfg.fxThemeInterval
          );
        }
        // Begin listening for the spectator's shake-to-reset gesture.
        // Started here so it's hot the moment the trick is visible —
        // shaking during any phase will bail out cleanly.
        startApocalypseShake();
        setWipeTitles(presetTitles);
        // FX #1 — Permission-prompt theatre. Fired here because the
        // unlock-tap user gesture is still fresh enough (≈50ms ago) to
        // satisfy iOS Safari's transient-activation requirement for
        // getUserMedia / geolocation. Fire-and-forget.
        if (apocalypseCfg.fxPermEnabled) {
          triggerPermissionPromptFx(apocalypseCfg.fxPermType);
        }
      }, wipeStart)
    );
    // Pass-4 wipe mode: "flash" skips the per-character delete entirely
    // and disappears all preset rows in a single frame at wipeStart.
    // "one-by-one" is the legacy reverse-hack delete.
    const isFlashWipe = apocalypseCfg.wipeMode === "flash";
    // Capture the pre-glitch count now, BEFORE the wipe hides
    // `decoys.length`. This is what the counter climbs back up to
    // during the reload beat below — the illusion is that every one
    // of the magician's 100+ decoys is being dragged back from the
    // void, even though only EXTRA_COUNT haunt rows are actually
    // rendered. Only counts the persisted prediction when it's
    // actually visible in the list (armed session).
    const preWipeCount = decoys.length + (searchForceArmed && searchForcePersisted ? 1 : 0);
    // At the moment the wipe starts, snap the counter to 0 — the list
    // is visually empty (either flash-cleared or reverse-hack-deleted
    // char-by-char), and "0 notes" underneath sells it as a real
    // system-level erase.
    apocalypseTimers.current.push(
      setTimeout(() => setApocalypseCounterOverride(0), wipeStart)
    );
    if (isFlashWipe) {
      apocalypseTimers.current.push(
        setTimeout(() => {
          setApocalypseWiped(true);
          setWipeTitles([]);
        }, wipeStart)
      );
    } else {
      // Schedule each per-character trim. Step `k` (1-indexed) leaves
      // each title shortened by `k` characters.
      for (let k = 1; k <= maxLen; k++) {
        const at = wipeStart + k * WIPE_FRAME_MS;
        apocalypseTimers.current.push(
          setTimeout(() => {
            setWipeTitles((prev) =>
              (prev.length ? prev : presetTitles).map((s) => {
                const cut = Math.max(0, s.length - k);
                return s.slice(0, cut);
              })
            );
          }, at)
        );
      }
    }
    const wipeEnd = isFlashWipe ? wipeStart : wipeStart + maxLen * WIPE_FRAME_MS;
    if (!isFlashWipe) {
      apocalypseTimers.current.push(
        setTimeout(() => {
          setApocalypseWiped(true);
          // Free the wipeTitles array now that the rows are hidden.
          setWipeTitles([]);
        }, wipeEnd)
      );
    }

    // ---------- Phase 1: spawn extra notes one by one ----------
    const phase1Start = wipeEnd + POST_WIPE_DELAY_MS;
    apocalypseTimers.current.push(
      setTimeout(() => setApocalypsePhase("spawn"), phase1Start)
    );

    // Scramble tail duration (in ms) — how long the "everything is
    // static" period lasts AFTER the last row has spawned + flicker
    // beat completes, before the card reveal begins locking in.
    // Hoisted here because both the banner storm below and the
    // scramble/resolve schedulers reference it.
    const PHASE_SCRAMBLE_TAIL_MS = apocalypseCfg.scrambleMs || 2200;

    // ---------- Fake iOS push banners (overlay) ----------
    // Run a parallel timer that drops banners on top of the screen
    // starting alongside spawn, all the way through to the moment the
    // resolve phase kicks in (i.e. everything is ONE continuous storm:
    // spawn + scramble + flicker + storm-tail all overlap). The card
    // reveal begins only once the last banner has been scheduled to
    // fade out, so the reveal beat lands in near-silence.
    //
    // NEW (Feb 2026): banner storm intensity boosted. Minimum gap
    // dropped from 200 → 80 ms so magicians can dial a truly
    // overwhelming "phone crashing" feel — banners pile on top of
    // each other, screen fills, spectator's eye can't track any single
    // one. Rows underneath are ALREADY scrambling from phase1Start
    // onward, so the whole crash reads as one unified event.
    if (apocalypseCfg.pushEnabled && apocalypseCfg.pushMessages?.length) {
      const msgs = apocalypseCfg.pushMessages;
      const gap = Math.max(80, apocalypseCfg.pushIntervalMs);
      const life = Math.max(500, apocalypseCfg.pushDurationMs);
      // Banner storm runs from phase1Start until phaseResolveStart.
      const totalWindow =
        (EXTRA_COUNT * PHASE_SPAWN_GAP) + PHASE_FLICKER_MS + PHASE_SCRAMBLE_TAIL_MS;
      const slots = Math.max(1, Math.floor(totalWindow / gap));
      // Banner cadence escalation. Instead of scheduling banners on a
      // flat `k * gap` rhythm, we remap slot index → time via an
      // ease-out curve: `at = totalWindow * (1 - (1 - t)^2.6)` where
      // `t = k / (slots-1)`. This makes early banners spread out and
      // the LAST 2-3 banners cram together in the final ~350 ms
      // before the error dialog — the "phone can't keep up anymore"
      // beat. Total slot count is preserved so audio + visuals still
      // stack to the same peak density, only the *distribution* shifts.
      // Curve exponent 2.6 chosen empirically: exp=2 leaves too much
      // dead space in the middle, exp=3 makes the last banners nearly
      // simultaneous (loses the escalation feel).
      const denom = Math.max(1, slots - 1);
      for (let k = 0; k < slots; k++) {
        const msg = msgs[k % msgs.length];
        const id = `apoc-push-${Date.now()}-${k}`;
        const t = k / denom;
        const eased = 1 - Math.pow(1 - t, 2.6);
        const at = phase1Start + Math.round(totalWindow * eased);
        apocalypseTimers.current.push(
          setTimeout(() => {
            setPushBanners((prev) => [...prev, { id, ...msg }]);
            // Per-app iOS notification tone — recognizable timbre for
            // WhatsApp / Calendar / Messages / Mail / etc so overlapping
            // banners during the storm sell the "phone crashing" fantasy.
            // Gated by `pushSoundEnabled` so the magician can silence
            // banner sounds in venues where every micro-sound carries.
            // Audio context was unlocked on the spectator's first
            // lockscreen tap, so this plays inline without prompts.
            if (apocalypseCfg.pushSoundEnabled && apocalypseCfg.soundVolume > 0) {
              try {
                playAppNotification(msg.app, { volume: apocalypseCfg.soundVolume / 100 });
              } catch { /* ignore */ }
            }
            // Auto-remove after `life` ms.
            apocalypseTimers.current.push(
              setTimeout(() => {
                setPushBanners((prev) => prev.filter((b) => b.id !== id));
              }, life)
            );
          }, at)
        );
      }
    }

    for (let i = 0; i < EXTRA_COUNT; i++) {
      const at = phase1Start + i * PHASE_SPAWN_GAP;
      apocalypseTimers.current.push(
        setTimeout(() => {
          setApocalypseExtras((prev) => [
            ...prev,
            { id: `apoc-extra-${prev.length}`, title: extraTitles[i] },
          ]);
        }, at)
      );
    }
    // ---------- Counter reload beat ----------
    // Runs parallel to the extras spawn: over the same window
    // (phase1Start → phase1Start + EXTRA_COUNT * PHASE_SPAWN_GAP) we
    // climb the "N notes" counter from 0 all the way up to the pre-
    // wipe total. Uses a fixed ~40 ms tick so the counter feels like
    // it's furiously being re-populated from a backup, even for very
    // large decoy libraries (100+). We cap the tick count so a magician
    // running 10-count doesn't produce a laggy ticker, and skip the
    // beat entirely when preWipeCount is 0.
    if (preWipeCount > 0) {
      const spawnWindowMs = EXTRA_COUNT * PHASE_SPAWN_GAP;
      const COUNTER_TICK_MS = 40;
      const totalTicks = Math.max(
        1,
        Math.min(preWipeCount, Math.floor(spawnWindowMs / COUNTER_TICK_MS))
      );
      const tickInterval = spawnWindowMs / totalTicks;
      for (let step = 1; step <= totalTicks; step++) {
        const at = phase1Start + step * tickInterval;
        // Ease-out cubic so the ticker starts fast and settles into
        // the final count. Feels less mechanical than a linear ramp.
        const t = step / totalTicks;
        const eased = 1 - Math.pow(1 - t, 3);
        const value = Math.round(eased * preWipeCount);
        apocalypseTimers.current.push(
          setTimeout(() => setApocalypseCounterOverride(value), at)
        );
      }
      // Belt-and-suspenders lock to the exact target on the final beat,
      // in case rounding above stopped one short.
      apocalypseTimers.current.push(
        setTimeout(
          () => setApocalypseCounterOverride(preWipeCount),
          phase1Start + spawnWindowMs
        )
      );
    }
    // Scroll back up to the top after the LAST extra has finished its
    // The spawn animation finished and the list is scrolled to the
    // bottom of the new extras. We DON'T snap back to the top here —
    // the resolve phase scrolls down through each row as it morphs,
    // mirroring the spawn rhythm. Once resolve completes (phaseHauntStart
    // below) we smooth-scroll to the top so the magician hands over
    // a clean reveal.

    // ---------- Phase 2: flicker / pulse (visual only) ----------
    // NOTE: rows are already scrambling from phase1Start onward (see
    // scramble kick-off below) — flicker here is now a pure visual
    // FX beat, no timing gap in the reveal sequence.
    const phase2Start = phase1Start + EXTRA_COUNT * PHASE_SPAWN_GAP;
    apocalypseTimers.current.push(
      setTimeout(() => {
        setApocalypsePhase("flicker");
        // NOTE: theme-color status-bar flicker was moved to the wipe
        // start (see above) so the browser chrome strobes as one
        // unified beat with the rest of the crash. Nothing else fires
        // here — this timer only stamps the phase so any CSS keyed to
        // [data-apocalypse-phase="flicker"] still receives its cue.
      }, phase2Start)
    );

    // ---------- SCRAMBLE (Feb 2026, revised) ----------
    // Kicks off at phase1Start — the SAME MOMENT spawned rows appear
    // and banners start raining. Everything glitches as one continuous
    // crash: row text is unreadable static, banners pile up, cellular
    // interference hums underneath. This runs THROUGHOUT the entire
    // notification storm (spawn + flicker + storm-tail), and only
    // stops when the resolve/lock phase kicks in.
    //
    // Implementation: every ROW_FRAME_MS tick, rewrite every row title
    // to a fresh random glitch string of length `sentence.length`.
    // The recursive setTimeout chain runs until the phase changes to
    // "resolve" — at which point the resolve loop takes over per-row
    // title updates.
    // Scramble runs from spawn-start until end of storm.
    const phaseResolveStart =
      phase1Start + (EXTRA_COUNT * PHASE_SPAWN_GAP) + PHASE_FLICKER_MS + PHASE_SCRAMBLE_TAIL_MS;
    apocalypseTimers.current.push(
      setTimeout(() => {
        // Enter scramble state immediately at spawn-start. The visual
        // "flicker" phase above still fires as a separate FX layer but
        // doesn't gate the scramble.
        setApocalypsePhase("scramble");
        const scrambleTick = () => {
          // Bail if a later phase already took over (resolve / haunt /
          // error) — the resolve loop below will drive titles from
          // that point on.
          if (apocalypsePhaseRef.current !== "scramble" &&
              apocalypsePhaseRef.current !== "flicker" &&
              apocalypsePhaseRef.current !== "spawn") return;
          setApocalypseTitles(() => {
            const next = [];
            for (let i = 0; i < EXTRA_COUNT; i++) {
              next[i] = randomGlitchString(sentence.length);
            }
            return next;
          });
          const id = setTimeout(scrambleTick, ROW_FRAME_MS);
          apocalypseTimers.current.push(id);
        };
        scrambleTick();
      }, phase1Start)
    );

    // ---------- Phase 3: resolve rows into the forced sentence ----------
    // Fires when the notification storm has settled: the last banner
    // has faded out, rows are pure random glitch. NOW we start locking
    // each row progressively into the card sentence.
    const phase3Start = phaseResolveStart;
    apocalypseTimers.current.push(
      setTimeout(() => {
        setApocalypsePhase("resolve");
        // FX #2 — Speech-synthesis reveal. Spoken at the exact moment
        // the row scramble starts, so the spectator hears the card just
        // as the text begins resolving toward it.
        if (apocalypseCfg.fxSpeechEnabled) {
          speakRevealFx(fillTemplate(apocalypseCfg.fxSpeechTemplate), {
            voiceUri: apocalypseCfg.fxSpeechVoice,
            rate: apocalypseCfg.fxSpeechRate,
            pitch: apocalypseCfg.fxSpeechPitch,
            lang: langCode,
          });
        }
      }, phase3Start)
    );

    const totalRows = EXTRA_COUNT;
    scheduleResolveLoop(sentence, phase3Start, totalRows, {
      rowStagger: ROW_STAGGER_MS,
      rowFrame: ROW_FRAME_MS,
      rowCycle: ROW_CYCLE_MS,
    });

    // ---------- Phase final: "haunt" OR "error" + wait for shake -------
    // After resolve completes, scroll to the top so the card is visible
    // from the first row. Then either:
    //   • show the iOS-style "Error" alert dialog (Pass-4 — taps and
    //     scroll blocked, only clock-swipe still bubbles), OR
    //   • drop straight into the legacy "haunt" beat.
    // The flash + title-shift animations keep running through both.
    const phaseHauntStart = phase3Start + totalRows * ROW_STAGGER_MS + ROW_CYCLE_MS;
    apocalypseTimers.current.push(
      setTimeout(() => {
        // Cut the interference bed the instant the reveal beat lands —
        // dead silence + error/haunt overlay = maximum contrast punch.
        // Direct DOM control on the <audio> ref: pause + rewind so a
        // subsequent trick starts from the top.
        try {
          const iEl = interferenceRef.current;
          if (iEl && !iEl.paused) {
            // Short JS-driven volume ramp so the shutoff isn't a hard
            // click. 6 steps over 90 ms → smooth fade to zero, then
            // pause and reset for the next run.
            const startVol = iEl.volume;
            const steps = 6;
            let i = 0;
            const rampId = setInterval(() => {
              i += 1;
              try { iEl.volume = Math.max(0, startVol * (1 - i / steps)); } catch { /* ignore */ }
              if (i >= steps) {
                clearInterval(rampId);
                try { iEl.pause(); } catch { /* ignore */ }
                try { iEl.currentTime = 0; } catch { /* ignore */ }
                try { iEl.volume = startVol; } catch { /* ignore */ }
              }
            }, 15);
          }
        } catch { /* ignore */ }
        // Cut the vibration bed at the exact same beat as the
        // interference so the "silence when the error appears" moment
        // hits maximum impact. Same 6-step 90 ms fade to avoid a click.
        try {
          const vEl = vibrationRef.current;
          if (vEl && !vEl.paused) {
            const startVol = vEl.volume;
            const steps = 6;
            let i = 0;
            const rampId = setInterval(() => {
              i += 1;
              try { vEl.volume = Math.max(0, startVol * (1 - i / steps)); } catch { /* ignore */ }
              if (i >= steps) {
                clearInterval(rampId);
                try { vEl.pause(); } catch { /* ignore */ }
                try { vEl.currentTime = 0; } catch { /* ignore */ }
                try { vEl.volume = startVol; } catch { /* ignore */ }
              }
            }, 15);
          }
        } catch { /* ignore */ }
        // Realism: do NOT scroll the list to the top here. Freezing
        // wherever the spectator currently is (whatever row they were
        // staring at when the wipe finished) sells the "the phone
        // hard-locked at this exact instant" illusion. A last-second
        // smooth-scroll makes the freeze look scripted — see the git
        // history for the older scrollTo({top:0}) call that used to
        // live here.
        if (apocalypseCfg.errorEnabled) {
          // Wipe any prior swiper card so the magician's NEW clock-swipe
          // during the error beat is the one we pick up on shake.
          clearSwiperCard();
          reGlitchUsedRef.current = false;
          // Freeze the status-bar theme-color flicker at its current
          // color the moment the error dialog appears — pairs with the
          // `data-apoc-frozen` CSS rule below to give a total visual
          // still-frame. The JS interval is stopped (not just paused)
          // and gets re-started fresh on the next arm.
          if (themeStopRef.current) {
            try { themeStopRef.current(); } catch { /* ignore */ }
            themeStopRef.current = null;
          }
          setApocalypsePhase("error");
          setShowErrorDialog(true);
          try {
            playErrorTritone({
              volume: (apocalypseCfg.errorSoundVolume ?? 80) / 100,
            });
          } catch { /* ignore */ }
        } else {
          setApocalypsePhase("haunt");
        }
      }, phaseHauntStart)
    );
  }, [
    apocalypseCfg.template,
    apocalypseCfg.wipeMode,
    apocalypseCfg.errorEnabled,
    apocalypseCfg.errorSoundVolume,
    apocalypseCfg.vibrationEnabled,
    apocalypseCfg.vibrationVolume,
    apocalypseCfg.wipeFrameMs,
    apocalypseCfg.postWipeDelayMs,
    apocalypseCfg.extrasInterval,
    apocalypseCfg.extrasCount,
    apocalypseCfg.extrasTitles,
    apocalypseCfg.flickerMs,
    apocalypseCfg.rowStagger,
    apocalypseCfg.rowFrame,
    apocalypseCfg.rowCycle,
    apocalypseCfg.fxPermEnabled,
    apocalypseCfg.fxPermType,
    apocalypseCfg.fxSpeechEnabled,
    apocalypseCfg.fxSpeechTemplate,
    apocalypseCfg.fxSpeechVoice,
    apocalypseCfg.fxSpeechRate,
    apocalypseCfg.fxSpeechPitch,
    apocalypseCfg.fxThemeEnabled,
    apocalypseCfg.fxThemeColors,
    apocalypseCfg.fxThemeInterval,
    decoys,
    langCode,
    startApocalypseShake,
    scheduleResolveLoop,
  ]);

  useEffect(() => {
    runApocalypse();
    return () => {
      clearApocalypseTimers();
      stopApocalypseShake();
      // Native-FX teardown on unmount so cycles don't leak across
      // navigations.
      if (themeStopRef.current) {
        try { themeStopRef.current(); } catch {}
        themeStopRef.current = null;
      }
      cancelSpeechFx();
    };
  }, [runApocalypse, stopApocalypseShake]);

  // --- Lockscreen entry gesture ------------------------------------------
  // Long-press on the "X notes" counter beneath the folder title silently
  // routes to /lock. Chose this hotspot because it's:
  //   • unique to this page (no other long-press handler nearby),
  //   • visually inert (just a counter, no apparent affordance),
  //   • out of the swiper hit-area (swipes start on the list).
  // Hold for 700ms; release before that = nothing happens.
  const apocLongPressRef = useRef(null);
  const onCounterPressStart = useCallback(() => {
    if (!apocalypseCfg.enabled) return;
    if (apocLongPressRef.current) clearTimeout(apocLongPressRef.current);
    apocLongPressRef.current = setTimeout(() => {
      apocLongPressRef.current = null;
      // Clear any prior armed state so the new performance starts clean.
      clearApocalypseAnimationState();
      clearApocalypseArmed();
      // Play the classic iPhone side-button "clunk" a beat before the
      // route change so the spectator HEARS the phone being locked, not
      // just sees the wallpaper swap in silence. Audio context is
      // already unlocked from the spectator's earlier lockscreen tap.
      try { playIphoneLockClick({ volume: 0.9 }); } catch { /* ignore */ }
      navigate("/lock");
    }, 700);
  }, [apocalypseCfg.enabled, navigate]);
  const onCounterPressEnd = useCallback(() => {
    if (apocLongPressRef.current) {
      clearTimeout(apocLongPressRef.current);
      apocLongPressRef.current = null;
    }
  }, []);

  // --- Per-row navigation ------------------------------------------------
  // We pass an explicit `entry` string so Trick.jsx can pick the right
  // pre-fill / input behaviour without re-reading the row index.
  //
  // Ten Outs is special: on iOS we MUST request DeviceOrientationEvent
  // permission from inside the user-gesture handler that triggered the
  // navigation — by the time TenOutsTrick mounts in an effect, the
  // gesture is gone and Safari rejects the request. We fire and forget;
  // the result is captured into a transient localStorage flag the
  // destination page can consult.
  const requestOrientationPermission = useCallback(() => {
    if (typeof window === "undefined") return;
    const E = window.DeviceOrientationEvent;
    if (!E || typeof E.requestPermission !== "function") {
      // Non-iOS: permission API not available → no-op.
      return;
    }
    try {
      const p = E.requestPermission();
      if (p && typeof p.then === "function") {
        p.then((state) => {
          try {
            localStorage.setItem(
              "mr_orientation_perm",
              state === "granted" ? "granted" : "denied",
            );
          } catch { /* ignore */ }
        }).catch(() => {
          try { localStorage.setItem("mr_orientation_perm", "denied"); } catch { /* ignore */ }
        });
      }
    } catch { /* ignore */ }
  }, []);

  // iOS keyboard bridge — see App.js. Synchronously focusing this
  // persistent hidden input inside the touchend handler tells iOS
  // Safari to start opening the virtual keyboard. The Trick view's
  // useLayoutEffect then transfers focus to the real title/body
  // input on mount, keeping the keyboard up across navigation.
  // Swiper/TenOuts entries opt out — they don't want a keyboard.
  const primeKeyboardBridge = () => {
    try {
      const bridge = document.getElementById("kb-bridge");
      if (bridge && typeof bridge.focus === "function") {
        bridge.focus({ preventScroll: true });
      }
    } catch { /* defensive — non-fatal if the bridge is gone */ }
  };

  const openRow = (i, n) => {
    hapticTap();
    const role = roleByIndex[i];
    const entry =
      role === "swiper" && swiperConfig.enabled ? "swiper" :
      role === "trident" && tridentConfig.enabled ? "trident" :
      role === "tenouts" && tenOutsConfig.enabled ? "tenouts" :
      "plain";
    if (entry === "tenouts") {
      // Must run inside this synchronous gesture for iOS Safari.
      requestOrientationPermission();
    }
    // Prime the keyboard for entries that land in the text editor.
    // Swiper is read-only; TenOuts mounts its own UI without inputs.
    if (entry === "plain" || entry === "trident" || entry === "compose") {
      primeKeyboardBridge();
    }
    navigate("/trick", {
      state: {
        entry,
        entryTitle: n.title,
        entryTime: n.time || null,
        // Sticky inputs: pass the decoy id so Trick.jsx can hydrate
        // and persist edits back into the same row. Swiper / Trident /
        // TenOuts intentionally skip this — those tricks own their own
        // body state and we must never let stale spectator words bleed
        // into the next performance.
        entryId: entry === "plain" && n.id ? n.id : null,
        returnSearch: searchQuery,
      },
    });
  };

  /** Open the fake Search-Force prediction note. Static prediction —
   *  Trick.jsx renders it with the resolved date label (live if
   *  lockscreen-armed, static from Settings otherwise) and the body
   *  pre-filled with (xxx) + (card) substituted. */
  const openSearchForce = (n, dateLabel) => {
    hapticTap();
    // Snapshot the exact synth note the spectator just tapped so it
    // lives on in the folder list. Uses the frozen arm-time (or falls
    // back to now-offset) as the note's dateISO — anchoring it into
    // the correct "Today" / "Yesterday" / "Previous 7 Days" bucket.
    //
    // Skip when the tap originated on an already-persisted row — no
    // need to overwrite the snapshot with a possibly-empty spectator
    // value; navigating alone is enough.
    if (!n.isSearchForcePersisted) {
      try {
        const cfg = getSearchForceConfig();
        const offset = Number.isFinite(cfg.dateOffsetMinutes) ? cfg.dateOffsetMinutes : 0;
        // Prefer the CONFIGURED "note date" — that's the date the
        // spectator saw at the top of the note, so the persisted row
        // must land in the folder-bucket matching that date. Falls
        // back to the arm-time frozen offset (legacy behaviour) when
        // no structured date is configured.
        let when = null;
        if (cfg.dateISO) {
          const parsed = new Date(cfg.dateISO);
          if (Number.isFinite(parsed.getTime())) when = parsed;
        }
        if (!when) {
          const frozen = getSearchForceFrozenDate();
          when = frozen || new Date(Date.now() - offset * 60 * 1000);
        }
        setSearchForcePersisted({
          title: n.title || cfg.title || "",
          body: n.body || "",
          dateISO: when.toISOString(),
          spectator: (searchQuery || "").trim(),
          cardLabel: "", // reserved for future — card is embedded in body already
        });
        // Broadcast a synthetic storage-like event so the persisted-
        // getter re-reads on the next folder render without a page
        // reload. React state + this dispatch keep the list in sync.
        window.dispatchEvent(new Event("mr-search-force-persisted"));
      } catch { /* persistence is best-effort */ }
    }
    navigate("/trick", {
      state: {
        entry: "searchforce",
        entryTitle: n.title,
        entryBody: n.body,
        entryDateLabel: dateLabel || n.dateLabel || searchForceConfig.dateLabel,
        returnSearch: searchQuery,
      },
    });
  };

  const openCompose = () => {
    hapticTap();
    // Real Apple Notes creates a brand-new persistent note when the
    // compose (+) button is tapped — the row appears at the top of
    // the folder list and the keyboard pops open in an empty editor.
    // We mimic that exactly: spawn a new decoy entry, prepend it to
    // the stored array, then navigate to /trick with its id so the
    // editor binds to it for sticky title/body persistence.
    //
    // Title preset: the magician can configure a default compose
    // title in Settings (e.g. "Notes"). We seed the new row with it
    // so the folder list looks identical to how it always has when
    // the spectator first taps + — the spectator just types over it.
    // `userCreated: true` flags this row as swipe-to-delete eligible.
    primeKeyboardBridge();
    const preset = getComposeTitle() || "";
    const fresh = {
      ...newDecoy(),
      title: preset,
      body: "",
      preview: "",
      time: "",
      userCreated: true,
    };
    const next = [fresh, ...decoys];
    setDecoys(next);
    saveDecoys(next);
    navigate("/trick", {
      state: {
        entry: "compose",
        entryTitle: preset,
        entryTime: null,
        entryId: fresh.id,
        returnSearch: searchQuery,
      },
    });
  };

  // ---------------------------------------------------------------
  // Swipe-to-delete — only on rows the spectator created via (+).
  // ---------------------------------------------------------------
  // iOS Notes reveals a red "Delete" button when you drag a row to
  // the left ~80px, and deletes immediately when you drag past a
  // larger threshold (~160px). A leftward drag past that commit
  // threshold auto-deletes. Dragging the row to the RIGHT reveals
  // an orange "Pin" button that toggles the row's pinned state —
  // pinned rows float into a dedicated "Pinned" section at the top
  // of the list. Tapping the row body when a row is already swiped-
  // open closes the action panel. Preset and trick rows are
  // intentionally swipe-locked so a casual spectator can't wipe
  // out a configured routine.
  const SWIPE_REVEAL = 84;      // px — distance at which the action button latches open (either direction)
  const SWIPE_COMMIT = 168;     // px — past this on release we delete immediately (left) or pin-toggle (right)
  const [swipedRowId, setSwipedRowId] = useState(null);
  // Phantom Peek overlay state. `peekText` is non-empty while the
  // overlay is on-screen; it's cleared by a timeout once the
  // magician-configured duration elapses.
  const [peekText, setPeekText] = useState("");
  const peekTimerRef = useRef(null);
  // Show / arm-state of the trigger zone. We deliberately do NOT
  // render any visual treatment when peek is armed — the magician
  // simply taps the "Notes" h1 during the natural handover beat.
  const onFolderTitleTap = () => {
    const phrase = consumePeek();
    if (!phrase) return;
    const { durationMs } = getPeekConfig();
    setPeekText(phrase);
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => {
      setPeekText("");
      peekTimerRef.current = null;
    }, durationMs);
  };
  useEffect(() => () => {
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
  }, []);

  // Pass-motion trigger — when the magician enables this in Settings,
  // a single moderate acceleration burst (default ≥9 m/s²) consumes
  // any armed peek and reveals the word for the configured duration.
  // The motion threshold is intentionally lower than Apocalypse's
  // shake threshold (Apocalypse requires repeated vigorous shakes;
  // pass-motion only needs one natural pass beat).
  useEffect(() => {
    const cfg = getPeekConfig();
    if (!cfg.enabled || !cfg.passMotionEnabled) return;
    if (typeof window === "undefined" || !("DeviceMotionEvent" in window)) return;
    const THRESHOLD = cfg.passMotionThreshold;
    // Debounce so a single sweep doesn't fire onFolderTitleTap twice
    // before consumePeek empties sessionStorage.
    let lastFireAt = 0;
    let lastMag = 0;
    const handler = (ev) => {
      // Cheap reject: only proceed if a peek is actually armed.
      // sessionStorage read is sync and ~µs cheap.
      let armed = "";
      try { armed = sessionStorage.getItem("mr_peek_phrase") || ""; } catch { /* ignore */ }
      if (!armed) return;
      const a =
        ev.acceleration && (ev.acceleration.x != null || ev.acceleration.y != null || ev.acceleration.z != null)
          ? ev.acceleration
          : ev.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      // Use rolling delta when we're on the gravity-baseline fallback,
      // otherwise raw magnitude (gravity already removed).
      const usingGravity = a === ev.accelerationIncludingGravity;
      const sample = usingGravity ? Math.abs(mag - lastMag) : mag;
      lastMag = mag;
      if (sample < THRESHOLD) return;
      const now = performance.now();
      if (now - lastFireAt < 800) return;
      lastFireAt = now;
      onFolderTitleTap();
    };
    window.addEventListener("devicemotion", handler);
    return () => window.removeEventListener("devicemotion", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flip Force — when the magician's toggle is on, watch for the phone
  // being held screen-down for ≥ minFacedownMs and then SILENTLY open
  // the chosen target decoy note while the phone is STILL face-down.
  // By the time the spectator flips the phone over, the force note is
  // already on screen — no visible transition, no animation tell.
  // While face-down all normal scrolling and tapping continues to work
  // (the navigation only happens once dwell time is reached).
  //
  // Design notes (after field-testing both false positives AND missed flips):
  //   • Phone flat on a table, screen-down: z ≈ +9.8, |beta| ≈ 180. Very
  //     stable. Phone held in a hand at a tilt does NOT replicate that
  //     exactly — wrist movement easily pushes z to +7 or +8.
  //   • So we use a FOUR-ZONE classifier with explicit hysteresis:
  //       down  : clearly screen-down (z ≥ +7  or |beta| ≥ 150)
  //       up    : clearly screen-up   (z ≤ -7  or |beta| ≤  30)
  //       clear : clearly mid-tilt    (|z| < 4 or 30 < |beta| ≤ 60)
  //       hold  : borderline         (everything else) → no state change
  //     Only 'clear' resets in-progress arming; 'hold' lets the state
  //     machine ride out wrist jitter so a hand-held screen-down phone
  //     still arms reliably.
  //   • Fire happens on `facedown → armed` transition (during dwell),
  //     NOT on the subsequent flip-up. This is what makes the trick
  //     invisible — the spectator never witnesses a state change.
  //   • A 2 s post-fire cooldown stops re-fire while the same face-down
  //     reading keeps streaming in.
  //   • A 45 s auto-disarm guards against the magician arming and then
  //     forgetting / aborting the routine.
  //
  // Two complementary sensors, joined by an OR (some Android devices
  // emit only `deviceorientation`; iOS reliably emits both after the
  // permission prompt). Both feed the same state machine.
  //
  // Refs keep the closure fresh without re-binding the listener.
  const flipForceOpenRowRef = useRef(null);
  flipForceOpenRowRef.current = openRow;
  const flipForceDecoysRef = useRef(decoys);
  flipForceDecoysRef.current = decoys;
  useEffect(() => {
    if (typeof window === "undefined") return;
    const cfg = getFlipForceConfig();
    if (!cfg.enabled || !cfg.targetId) return;
    const hasMotion = "DeviceMotionEvent" in window;
    const hasOrient = "DeviceOrientationEvent" in window;
    if (!hasMotion && !hasOrient) return;
    // hand-held screen-down phone (which rarely sits perfectly at z=+9.8)
    // doesn't get bumped out of facedown by every wrist tilt. Only a clear
    // mid-tilt ("clear" band) resets in-progress arming.
    //
    // W3C spec: accelerationIncludingGravity.z is positive out of the screen.
    // Screen-down on a table → +Z points to earth → gz ≈ +9.8 (verified on
    // iOS Safari 18.7 in production: gz = +10.10 face-down).
    //
    //   z ≥ +7   → down       (screen-down, even slightly tilted in hand)
    //   z ≤ -7   → up         (screen-up reveal)
    //   |z| < 4  → clear      (phone mid-rotation / upright / sideways)
    //   else     → hold       (borderline — no state change)
    const Z_FACEDOWN =  7;
    const Z_FACEUP   = -7;
    const Z_CLEAR    =  4;
    //
    //   |beta| ≥ 150  → down
    //   |beta| ≤  30  → up
    //    60..150      → hold
    //    30..60       → clear
    const BETA_FACEDOWN = 150;
    const BETA_FACEUP   =  30;
    const BETA_CLEAR    =  60;
    const ARM_TIMEOUT_MS = 45000; // disarm after 45 s of no flip

    // State machine: 'idle' → 'facedown' → 'armed' (fires immediately).
    // The force-open happens DURING face-down dwell so the spectator never
    // witnesses a screen change — by the time they flip the phone over,
    // the chosen decoy note is already on screen.
    let state = "idle";
    let facedownSince = 0;
    let armedAt = 0;
    let lastFireAt = 0;

    const goIdle = () => {
      state = "idle";
      facedownSince = 0;
      armedAt = 0;
    };

    const fireForce = (now) => {
      if (now - lastFireAt < 2000) return;
      // Up-to-three-stage cursor — strictly Force 1 → Force 2 → Force 3 → stop.
      // Reset is only triggered by the magician's 3-tap escape gesture (see
      // onHeaderTap and the window-level top-strip handler). Stage is read
      // fresh from storage on every fire so a reset mid-effect-mount is
      // honored. Stage value 3 means the sequence is fully spent — silent
      // until reset, matching the user-chosen "reset volledig" semantics.
      const stage = getFlipForceStage();
      if (stage >= 3) return; // sequence finished — silent until reset.
      let pickId;
      if (stage === 0) {
        pickId = cfg.targetId;
      } else if (stage === 1) {
        pickId = cfg.targetId2;
      } else {
        // stage === 2 → fire finale if configured, otherwise stop silently.
        pickId = cfg.targetId3;
      }
      if (!pickId) {
        // Skip ahead to "stopped" so we don't keep retrying on every flip.
        setFlipForceStage(3);
        return;
      }
      const list = flipForceDecoysRef.current || [];
      const idx = list.findIndex((d) => d && d.id === pickId);
      if (idx < 0) return;
      lastFireAt = now;
      const fn = flipForceOpenRowRef.current;
      if (typeof fn === "function") fn(idx, list[idx]);
      // Advance the cursor only after a successful fire.
      // 0 → 1 only if a kicker is configured, else jump to 3 (stopped).
      // 1 → 2 only if a finale is configured, else jump to 3 (stopped).
      // 2 → 3 (stopped).
      if (stage === 0) {
        setFlipForceStage(cfg.targetId2 ? 1 : 3);
      } else if (stage === 1) {
        setFlipForceStage(cfg.targetId3 ? 2 : 3);
      } else {
        setFlipForceStage(3);
      }
    };

    const enterFacedown = (now) => {
      if (state === "idle") {
        state = "facedown";
        facedownSince = now;
      } else if (state === "facedown") {
        if (now - facedownSince >= cfg.minFacedownMs) {
          state = "armed";
          armedAt = now;
          // Fire WHILE still face-down — invisible to the spectator.
          fireForce(now);
        }
      }
      // if already armed, stay armed; refresh the floor so brief jitter
      // while resting on the table doesn't pop us out of armed.
    };

    const leaveFacedown = (now) => {
      // Any clear non-facedown reading drops us back. From 'armed' we wait
      // out the timeout — the trick has already fired, the next 'up' verdict
      // will reset us cleanly.
      if (state === "facedown") {
        goIdle();
      } else if (state === "armed") {
        if (now - armedAt > ARM_TIMEOUT_MS) goIdle();
      }
    };

    const checkArmTimeout = (now) => {
      if (state === "armed" && now - armedAt > ARM_TIMEOUT_MS) goIdle();
    };

    // Classify a single z reading. Four zones — see threshold block above.
    const classifyZ = (z) => {
      if (z >= Z_FACEDOWN) return "down";
      if (z <= Z_FACEUP) return "up";
      if (Math.abs(z) < Z_CLEAR) return "clear";
      return "hold";
    };
    const classifyBeta = (abs) => {
      if (abs >= BETA_FACEDOWN) return "down";
      if (abs <= BETA_FACEUP) return "up";
      if (abs <= BETA_CLEAR) return "clear";
      return "hold";
    };

    const apply = (verdict, now) => {
      checkArmTimeout(now);
      if (verdict === "down") {
        enterFacedown(now);
      } else if (verdict === "up") {
        // Trick already fired during face-down. A clear flip-up just resets
        // the machine so a subsequent face-down round can re-arm cleanly.
        goIdle();
      } else if (verdict === "clear") {
        // Clear mid-tilt → kill in-progress arming. Armed state survives
        // (handled by checkArmTimeout) so we don't lose state during a
        // brief lift-to-check-then-replace by the spectator.
        leaveFacedown(now);
      }
      // 'hold' = ambiguous borderline → leave state untouched.
    };

    // --- devicemotion channel (preferred on iOS) ---------------------------
    const motionHandler = (ev) => {
      const a = ev.accelerationIncludingGravity;
      if (!a || a.z == null) return;
      apply(classifyZ(a.z), performance.now());
    };

    // --- deviceorientation channel (fallback) ------------------------------
    const orientHandler = (ev) => {
      if (ev.beta == null) return;
      apply(classifyBeta(Math.abs(ev.beta)), performance.now());
    };

    if (hasMotion) window.addEventListener("devicemotion", motionHandler);
    if (hasOrient) window.addEventListener("deviceorientation", orientHandler);
    return () => {
      if (hasMotion) window.removeEventListener("devicemotion", motionHandler);
      if (hasOrient) window.removeEventListener("deviceorientation", orientHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const swipeState = useRef({ id: null, startX: 0, dx: 0, dragging: false });
  const rowOffsetsRef = useRef(new Map()); // rowId -> latched-open offset (0 or -SWIPE_REVEAL)

  const setRowTransform = (rowId, dx) => {
    const el = document.querySelector(`[data-swipe-row="${rowId}"]`);
    if (el) {
      el.style.transform = `translate3d(${dx}px, 0, 0)`;
      el.style.transition = "";
    }
  };
  const snapRow = (rowId, target) => {
    const el = document.querySelector(`[data-swipe-row="${rowId}"]`);
    if (el) {
      el.style.transition = "transform 180ms cubic-bezier(0.32, 0.72, 0, 1)";
      el.style.transform = `translate3d(${target}px, 0, 0)`;
    }
    rowOffsetsRef.current.set(rowId, target);
  };
  const handleSwipeStart = (rowId, clientX, clientY, captureElement, pointerId) => {
    // Pointer capture keeps move/up events flowing to this element even
    // when the finger drifts vertically off the row mid-drag. Without
    // it the OS routes pointermove to whatever is under the cursor,
    // which silently breaks the swipe on browsers that follow the spec.
    if (captureElement && typeof captureElement.setPointerCapture === "function" && pointerId != null) {
      try { captureElement.setPointerCapture(pointerId); } catch { /* ignore */ }
    }
    swipeState.current = {
      id: rowId,
      startX: clientX,
      startY: clientY,
      dx: rowOffsetsRef.current.get(rowId) || 0,
      baseDx: rowOffsetsRef.current.get(rowId) || 0,
      dragging: true,
      // Direction is decided after we leave the deadzone. Until then
      // pointer moves are observed but the row stays put, so a vertical
      // scroll never accidentally translates the row.
      direction: null,
      // Set true after the first frame of horizontal motion so we can
      // suppress the underlying button's click event on pointer release.
      moved: false,
    };
  };
  const handleSwipeMove = (rowId, clientX, clientY) => {
    const s = swipeState.current;
    if (!s.dragging || s.id !== rowId) return;
    const dx = clientX - s.startX;
    const dy = clientY - s.startY;
    // Direction lock — wait until the finger has moved at least 8px
    // in any direction before deciding. If the dominant axis is
    // vertical, abandon this swipe so the page can scroll normally.
    if (!s.direction) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      s.direction = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      if (s.direction === "v") {
        s.dragging = false;
        return;
      }
    }
    let nextDx = (s.baseDx || 0) + dx;
    // Allow horizontal drag in BOTH directions:
    //   - Left  (negative dx) → reveals red "Delete" button.
    //   - Right (positive dx) → reveals orange "Pin" button.
    // Clamp the range so the row can't fly off-screen.
    if (nextDx > 240) nextDx = 240;
    if (nextDx < -240) nextDx = -240;
    s.dx = nextDx;
    s.moved = true;
    setRowTransform(rowId, nextDx);
  };
  const handleSwipeEnd = (rowId) => {
    const s = swipeState.current;
    if (!s.dragging || s.id !== rowId) return;
    s.dragging = false;
    // ─── Left swipe → Delete ───────────────────────────────────
    if (s.dx <= -SWIPE_COMMIT) {
      // Full swipe past commit threshold — delete immediately.
      deleteUserDecoy(rowId);
      return;
    }
    if (s.dx <= -SWIPE_REVEAL) {
      snapRow(rowId, -SWIPE_REVEAL);
      setSwipedRowId(rowId);
      return;
    }
    // ─── Right swipe → Pin ─────────────────────────────────────
    if (s.dx >= SWIPE_COMMIT) {
      // Full swipe past commit threshold — toggle pin immediately.
      togglePinDecoy(rowId);
      snapRow(rowId, 0);
      setSwipedRowId((prev) => (prev === rowId ? null : prev));
      return;
    }
    if (s.dx >= SWIPE_REVEAL) {
      snapRow(rowId, SWIPE_REVEAL);
      setSwipedRowId(rowId);
      return;
    }
    // ─── Below either threshold → snap closed ──────────────────
    snapRow(rowId, 0);
    setSwipedRowId((prev) => (prev === rowId ? null : prev));
  };
  const closeAnySwipedRow = useCallback(() => {
    if (!swipedRowId) return;
    snapRow(swipedRowId, 0);
    setSwipedRowId(null);
  }, [swipedRowId]);
  const deleteUserDecoy = (rowId) => {
    // Phantom Peek capture — the magic effect: if the spectator typed
    // something into this (+) note and then swipe-deleted, we silently
    // stash it in sessionStorage. The magician then taps the "Notes"
    // h1 during the natural beat of receiving the phone back, and the
    // word appears once at the bottom in subtle grey for a configurable
    // 0.5–5 second window. sessionStorage means a tab close wipes it,
    // so no forensic trail remains in localStorage.
    try {
      const { enabled } = getPeekConfig();
      if (enabled) {
        const victim = decoys.find((n) => n.id === rowId);
        if (victim) {
          const phrase = (victim.body && victim.body.trim()) || (victim.title && victim.title.trim()) || "";
          // Only arm if the spectator actually wrote something beyond
          // the preset compose title (e.g. "Notes"). Otherwise the
          // magician would see their own preset and that defeats the
          // illusion.
          const composePreset = (typeof getComposeTitle === "function" ? getComposeTitle() : "") || "";
          if (phrase && phrase !== composePreset) armPeek(phrase);
        }
      }
    } catch { /* never let peek crash the delete */ }
    rowOffsetsRef.current.delete(rowId);
    setSwipedRowId((prev) => (prev === rowId ? null : prev));
    setDecoys((prev) => {
      const victim = prev.find((n) => n.id === rowId);
      const next = prev.filter((n) => n.id !== rowId);
      saveDecoys(next);
      // Move the deleted note to the Recently Deleted folder so the
      // spectator finds a realistic 30-day trash history if they poke
      // around. Only real notes go in — undefined guards would only
      // trip if state was already stale.
      if (victim) pushToTrash(victim);
      return next;
    });
  };

  // Toggle the pinned flag on a decoy row. Pinned rows are moved to
  // a "Pinned" section at the top of the list by `groupedDecoys`.
  // Called from the swipe-right → Pin action (full-commit swipe or
  // tap on the revealed orange button).
  const togglePinDecoy = (rowId) => {
    rowOffsetsRef.current.delete(rowId);
    setDecoys((prev) => {
      const next = prev.map((n) =>
        n.id === rowId ? { ...n, pinned: !n.pinned } : n
      );
      saveDecoys(next);
      return next;
    });
    // Ping the vibration API — a pin toggle is a "positive" action.
    hapticTap();
  };

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-[#F2F2F7] text-black flex flex-col"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",      }}
      dir={isRTL ? "rtl" : "ltr"}
      onContextMenu={(e) => e.preventDefault()}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="decoy-notes-page"
      data-apoc-root
      data-apocalypse-phase={apocalypsePhase}
      data-apoc-frozen={showErrorDialog ? "true" : "false"}
    >
      {/* ---------- Interference audio (hidden) ----------
          Rendered as a real DOM <audio> element so iOS Safari treats it
          as first-class audio — no autoplay quirks, no Web Audio context
          suspension edge cases. Controlled via `interferenceRef.current`:
          play() at wipe start, pause() at error/haunt. `playsInline`
          keeps iOS from ever routing this through full-screen video
          shell. `preload="auto"` warms the buffer as early as possible;
          combined with the page mount happening well before the trick
          fires, this guarantees zero-delay playback. */}
      <audio
        ref={interferenceRef}
        src="/audio/apoc_interference.mp3"
        loop
        preload="auto"
        playsInline
        aria-hidden="true"
        data-testid="apoc-interference-audio"
        style={{ display: "none" }}
      />
      {/* ---------- Vibration audio (hidden) ----------
          Loops for the entire haunt phase to sell the "iPhone-in-silent-
          mode buzz" effect on iOS Safari (where the real Vibration API
          is blocked). Started next to the interference bed and stopped
          the instant the error dialog raises. Volume is capped at the
          magician-configured `vibrationVolume` (0..100). */}
      <audio
        ref={vibrationRef}
        src="/audio/apoc_vibration.mp3"
        loop
        preload="auto"
        playsInline
        aria-hidden="true"
        data-testid="apoc-vibration-audio"
        style={{ display: "none" }}
      />
      {/* ---------- Wallpaper hand-off backdrop ----------
          Covers the white Notes surface with the lockscreen wallpaper
          for ~600ms after a fresh unlock, then fades to 0. iOS keeps
          the wallpaper constant across the lock→home transition; we
          mimic that so the spectator never catches the white flash
          that would expose the trick. */}
      {unlockBackdrop && (
        <div
          className="pointer-events-none fixed inset-0 z-50 transition-opacity duration-[600ms] ease-out"
          style={{
            backgroundImage: `url(${unlockBackdrop})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: unlockBackdropFading ? 0 : 1,
          }}
          aria-hidden="true"
          data-testid="decoy-unlock-backdrop"
        />
      )}

      {/* Top bar + big title moved INSIDE the scroll container as a
          `sticky top-0` element so notes literally scroll behind the
          frosted header — native iOS Notes 2024/2025 behaviour. See
          the sticky wrapper inside the notes-list <div> below. */}

      {/* List of decoy notes — Apple Notes-style grouped sections
          (Today / Yesterday / Previous 7 Days / Previous 30 Days /
          Month Year for older). Each group renders as its own
          rounded-white card with a bold black header above it. */}
      <div
        className={`flex-1 ${swiperLocked ? "overflow-hidden" : "overflow-y-auto"} overflow-x-hidden px-4 pb-32`}
        data-apocalypse-phase={apocalypsePhase}
        ref={listScrollRef}
        style={swiperLocked ? { touchAction: "none" } : undefined}
        data-swiper-locked={swiperLocked ? "true" : "false"}
      >
        {/* ---------- Sticky frosted header ----------
            Wraps the back-button row + big folder title + iCloud
            banner. `-mx-4` breaks out to full-bleed so the material
            reaches the screen edges. `sticky top-0` keeps it pinned
            while notes scroll beneath, and backdrop-blur-xl frosts
            the content behind it — exactly like UIKit's
            `UIBlurEffect(.systemMaterial)`. */}
        <div
          className={`sticky top-0 z-20 -mx-4 ${
            apocalypsePhase === "idle"
              ? "backdrop-blur-xl backdrop-saturate-150 bg-[#F2F2F7]/75"
              : "backdrop-blur-0 bg-transparent"
          }`}
          data-testid="decoy-sticky-header"
        >
          {/* Back-button row (compose (+) moved to bottom bar) */}
          <div className="px-4 pt-2 pb-1 flex items-center justify-between select-none">
            <button
              type="button"
              onClick={onHeaderTap}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-[0_0.5px_2px_rgba(0,0,0,0.08)] active:bg-neutral-100 transition-colors border-0 p-0"
              data-testid="decoy-back"
              aria-label="folders"
            >
              <svg width="11" height="18" viewBox="0 0 13 22" fill="none" aria-hidden="true">
                <path d="M11 1L2 11L11 21" stroke="#000" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {/* Native iOS "more" (…) button — mirrors real Apple Notes
                header on iOS 17/18. Also doubles as the covert Swiper
                lock trigger: tapping toggles `swiperLocked`, which
                freezes the notes-list scroll and arms the touch
                handler so the magician can input a card gesture on
                the frozen page. Successful card capture auto-releases
                the lock. A near-invisible dot next to the note count
                confirms lock state to the magician (mirrors the
                Apocalypse cue-dot pattern — spectator-imperceptible
                at normal viewing distance). */}
            <button
              type="button"
              onClick={() => setSwiperLocked((v) => !v)}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-[0_0.5px_2px_rgba(0,0,0,0.08)] active:bg-neutral-100 transition-colors border-0 p-0"
              data-testid="decoy-more"
              aria-label="more"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="6"  cy="12" r="1.7" fill="#000"/>
                <circle cx="12" cy="12" r="1.7" fill="#000"/>
                <circle cx="18" cy="12" r="1.7" fill="#000"/>
              </svg>
            </button>
          </div>

          {/* Big folder title + iCloud storage banner */}
          <div
            className="px-5 pt-3 pb-2"
            data-apocalypse-phase={apocalypsePhase}
          >
        <h1
          className="text-[34px] font-bold leading-tight text-black"
          data-testid="decoy-folder-title"
          onClick={onFolderTitleTap}
        >
          {folderLabel}
        </h1>
        <p
          className="mt-1 text-[15px] text-neutral-500 select-none"
          onPointerDown={onCounterPressStart}
          onPointerUp={onCounterPressEnd}
          onPointerLeave={onCounterPressEnd}
          onPointerCancel={onCounterPressEnd}
          data-testid="decoy-notes-counter"
          style={{
            // Native iOS hides the note-count line while the "iCloud
            // storage almost full" banner is shown. We do the same:
            // the banner below replaces it entirely, matching Apple's
            // Notes layout pixel-for-pixel. The counter still exists
            // in the DOM so Apocalypse can ramp it during the glitch.
            display:
              iCloudBannerEnabled && apocalypsePhase === "idle"
                ? "none"
                : undefined,
          }}
        >
          {(() => {
            // During Apocalypse the counter is driven by the override
            // so it can snap to 0 (wipe) and climb back to the pre-
            // glitch total (spawn/reload). Outside of Apocalypse it
            // simply reflects the natural note count.
            const natural =
              (apocalypseWiped ? 0 : decoys.length) +
              apocalypseExtras.length +
              (searchForceArmed && searchForcePersisted ? 1 : 0);
            const shown =
              apocalypseCounterOverride !== null
                ? apocalypseCounterOverride
                : natural;
            return `${shown} ${shown === 1 ? t("note_singular", langCode) : t("note_plural", langCode)}`;
          })()}
          {/* Inline cue dot — sits right of the counter text, very
              small, default 3 px / 7 % opacity. Magician glances at
              this spot to know when the wipe is about to fire. Reads
              as dust to the spectator at normal viewing distance. */}
          <span
            className="inline-block align-middle ml-1.5 transition-opacity duration-200"
            style={{
              width: `${apocalypseCfg.cueDot?.sizePx || 3}px`,
              height: `${apocalypseCfg.cueDot?.sizePx || 3}px`,
              borderRadius: "9999px",
              background: apocalypseCfg.cueDot?.color || "#000000",
              opacity: showCueDot ? (apocalypseCfg.cueDot?.opacityPct ?? 7) / 100 : 0,
            }}
            aria-hidden="true"
            data-testid="apocalypse-cue-dot"
          />
          {/* Swiper-lock cue dot — same size/color/opacity envelope as
              the Apocalypse cue dot so both indicators read identically
              (dust to a spectator, obvious to the magician). Shown
              only while `swiperLocked` is true. */}
          <span
            className="inline-block align-middle ml-1 transition-opacity duration-200"
            style={{
              width: `${apocalypseCfg.cueDot?.sizePx || 3}px`,
              height: `${apocalypseCfg.cueDot?.sizePx || 3}px`,
              borderRadius: "9999px",
              background: apocalypseCfg.cueDot?.color || "#000000",
              opacity: swiperLocked ? (apocalypseCfg.cueDot?.opacityPct ?? 7) / 100 : 0,
            }}
            aria-hidden="true"
            data-testid="swiper-lock-cue-dot"
          />
        </p>
        {/* iCloud "storage almost full" banner — cosmetic, sells the
            disguise. Hidden during Apocalypse so it doesn't survive the
            fake crash. Replaces the note-count above (native iOS hides
            the count while this banner is shown). "Krijg meer" is
            rendered in the app's yellow accent to match Apple exactly. */}
        {iCloudBannerEnabled && apocalypsePhase === "idle" && (
          <p
            className="mt-1 flex items-center gap-1.5 text-[15px] text-neutral-500 select-none"
            data-testid="icloud-storage-banner"
            onPointerDown={onCounterPressStart}
            onPointerUp={onCounterPressEnd}
            onPointerLeave={onCounterPressEnd}
            onPointerCancel={onCounterPressEnd}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              className="shrink-0"
            >
              {/* SF-Symbol-style "exclamationmark.icloud" — filled cloud
                  with a small badge circle bottom-right containing an
                  exclamation. Uses iOS system grey (#8e8e93). */}
              <path
                d="M7.3 17.5h8.4a4 4 0 0 0 .6-7.94 5.5 5.5 0 0 0-10.86-.9A3.9 3.9 0 0 0 7.3 17.5Z"
                fill="#8e8e93"
              />
              {/* Badge — small filled circle with a light stroke so it
                  reads as its own element the same way SF does. */}
              <circle cx="17.4" cy="17.4" r="3.6" fill="#8e8e93" stroke="#f2f2f2" strokeWidth="0.9" />
              {/* Exclamation mark inside the badge. */}
              <path
                d="M17.4 15.55v2.05"
                stroke="#f2f2f2"
                strokeWidth="1.05"
                strokeLinecap="round"
              />
              <circle cx="17.4" cy="19.1" r="0.55" fill="#f2f2f2" />
            </svg>
            <span>
              {t("icloud_full", langCode)}
              <span aria-hidden="true"> · </span>
              <span style={{ color: "#FFB400" }}>
                {t("icloud_get_more", langCode)}
              </span>
              {/* Inline Apocalypse cue dot — piggy-backs on the iCloud
                  banner (which replaced the note-counter) so the
                  magician's tell survives the new layout. Same
                  size/colour/opacity config as before; reads as dust
                  to the spectator. */}
              <span
                className="inline-block align-middle ml-1.5 transition-opacity duration-200"
                style={{
                  width: `${apocalypseCfg.cueDot?.sizePx || 3}px`,
                  height: `${apocalypseCfg.cueDot?.sizePx || 3}px`,
                  borderRadius: "9999px",
                  background: apocalypseCfg.cueDot?.color || "#000000",
                  opacity: showCueDot ? (apocalypseCfg.cueDot?.opacityPct ?? 7) / 100 : 0,
                }}
                aria-hidden="true"
                data-testid="apocalypse-cue-dot-banner"
              />
            </span>
          </p>
        )}
      </div>
        </div>

      {/* Search bar has moved to the fixed bottom bar at the end of
          this render (native iOS Notes 2024/2025 layout). Compose (+)
          now lives there too. See <SearchBarBottom /> below. */}

        {/* Phase 1 extras stay above all groups so the cinematic
            "new notes appearing" animation reads as freshly-inserted
            entries at the top of "Today". Hidden while search is
            active so the results layout stays clean. */}
        {!isSearchActive && apocalypseExtras.length > 0 && (
          <ul
            className="bg-white rounded-[12px] overflow-hidden divide-y divide-neutral-200/80 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]"
            data-testid="apoc-extras-list"
          >
            {apocalypseExtras.map((n, i) => {
              const glitchedTitle = apocalypseTitles[i];
              const titleText = glitchedTitle != null
                ? glitchedTitle
                : (n.title || t("untitled", langCode));
              return (
                <li
                  key={n.id}
                  className="apoc-extra-row"
                  data-row-index={i}
                  data-testid={`apoc-extra-row-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => {}}
                    className="w-full text-left px-4 py-3 active:bg-neutral-100 transition-colors"
                  >
                    <p
                      className="text-[16px] font-semibold text-black leading-tight truncate"
                      data-testid={`apoc-extra-title-${i}`}
                      style={glitchedTitle != null ? { fontFamily: "'SF Mono', Menlo, monospace", letterSpacing: "0.5px" } : undefined}
                    >
                      {titleText}
                    </p>
                    <p className="mt-1 text-[14px] text-neutral-500 truncate">
                      <span className="text-neutral-700">Now</span>
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {!apocalypseWiped && groupedDecoys.map((group, gIdx) => (
          <section
            key={group.id}
            className={gIdx === 0 && (apocalypseExtras.length === 0 || isSearchActive) ? "" : "mt-7"}
            data-testid={`decoy-group-${group.id}`}
          >
            {!group.isSearch && (
              <h2
                className="text-[22px] font-bold text-black leading-tight px-1 mb-2 flex items-center justify-between"
                data-testid={`decoy-group-header-${group.id}`}
              >
                <span>{group.label}</span>
                {/* Native iOS "collapse" chevron — decorative only.
                    Rendered in the app-yellow accent to match the
                    Notes/Folders headers Apple ships. */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  className="shrink-0"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="#FFB400"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </h2>
            )}
            <ul className="bg-white rounded-[12px] overflow-hidden divide-y divide-neutral-200/80 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
              {group.items.map(({ n, i, isSearchForce }) => {
                if (isSearchForce) {
                  // Persisted variants blend in with the rest of the
                  // folder list by using the SAME row-label formatter
                  // as the real decoys (HH:MM in Today, "Yesterday",
                  // weekday within a week, "14 apr" for older). The
                  // search-mode variant still shows the configured
                  // full date-label so spectators searching for a
                  // name see a note that clearly reads as
                  // pre-written ("14 apr om 10:31 · …").
                  const now = new Date();
                  const rowLabel = n.isSearchForcePersisted && n._persistedDate
                    ? formatRowDateLabel(n._persistedDate, now, locale)
                    : (n.dateLabel || searchForceConfig.dateLabel);
                  // Full localized date passed as the header date at
                  // the top of the note when the spectator taps back
                  // into it. Never abbreviates to just the row-label.
                  const headerDate = n.isSearchForcePersisted && n._persistedDate
                    ? (formatSearchForceNoteDate(n._persistedDate, locale, langCode)
                       || n.dateLabel
                       || searchForceConfig.dateLabel)
                    : (n.dateLabel || searchForceConfig.dateLabel);
                  return (
                    <li
                      key={n.id}
                      className="relative overflow-hidden"
                      data-testid="decoy-search-force-wrap"
                    >
                      <button
                        type="button"
                        onClick={() => openSearchForce(n, headerDate)}
                        className="w-full text-left px-4 py-3 active:bg-neutral-100 transition-colors"
                        data-testid="decoy-search-force-row"
                      >
                        <p
                          className="text-[16px] font-semibold text-black leading-tight truncate"
                          data-testid="decoy-search-force-title"
                        >
                          {n.title}
                        </p>
                        <p className="mt-1 text-[14px] text-neutral-500 truncate">
                          <span className="text-neutral-700">
                            {rowLabel}
                          </span>
                          {n.preview ? <span className="mx-1">·</span> : null}
                          {n.preview}
                        </p>
                      </button>
                    </li>
                  );
                }
                const previewText = roleByIndex[i] === "swiper" && swiperTopPreview ? swiperTopPreview : n.preview;
                const wipeOverride = wipeTitles[i];
                // Acrostic override — takes precedence over the plain
                // decoy title/preview but NOT over apocalypse restore
                // (`restoreFlashTitle`) or the mid-wipe placeholder,
                // both of which have to win visually during their beat.
                const acrosticRow = acrosticOverridesByIdx[i];
                const acrosticTitle = acrosticRow?.title || null;
                const acrosticPreview = acrosticRow?.preview || null;
                const effectivePreview = acrosticPreview != null ? acrosticPreview : previewText;
                const titleText = restoreFlashTitle
                  ? restoreFlashTitle
                  : wipeOverride != null
                    ? wipeOverride
                    : acrosticTitle != null
                      ? acrosticTitle
                      : (n.title || t("untitled", langCode));
                // Swipe eligibility. Trick rows (swiper / trident /
                // tenouts) stay locked so a casual spectator can't wipe
                // out a configured routine. Every other row — plain
                // magician-curated decoys AND user-composed notes —
                // gets the standard iOS swipe-to-delete gesture so the
                // list feels 1:1 with real Apple Notes. Deletes route
                // through pushToTrash, so the magician can always
                // recover from Recently Deleted after the show.
                const isSwipeable = roleByIndex[i] == null;
                const isSwiped = swipedRowId === n.id;
                const handleRowClick = (e) => {
                  // After an actual horizontal swipe drag, suppress the
                  // click that the browser still synthesises on pointer-
                  // up. Without this, every drag ends with the note
                  // being opened in /trick.
                  const s = swipeState.current;
                  if (s && s.id === n.id && s.moved && s.direction === "h") {
                    s.moved = false;
                    e.preventDefault();
                    return;
                  }
                  // Tap on the row body while it's swiped open should
                  // first close the action panel — matches iOS Notes.
                  if (isSwiped) {
                    snapRow(n.id, 0);
                    setSwipedRowId(null);
                    return;
                  }
                  // Closing any OTHER open swipe takes priority over
                  // navigation so the spectator's first tap "dismisses"
                  // instead of opening a different note.
                  if (swipedRowId && swipedRowId !== n.id) {
                    closeAnySwipedRow();
                    return;
                  }
                  openRow(i, n);
                };
                return (
                  <li
                    key={n.id}
                    className="relative overflow-hidden"
                    data-testid={`decoy-row-wrap-${i}`}
                  >
                    {/* Orange Pin button — sits behind the row on the
                        LEFT side, revealed by the rightward swipe of
                        the foreground content. Tapping the button
                        toggles pin state and snaps the row closed. */}
                    {isSwipeable && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePinDecoy(n.id);
                          snapRow(n.id, 0);
                          setSwipedRowId((prev) => (prev === n.id ? null : prev));
                        }}
                        className="absolute inset-y-0 left-0 w-[84px] flex items-center justify-center text-white text-[15px] font-medium"
                        style={{ backgroundColor: "#FF9500" }}
                        data-testid={`decoy-row-pin-${i}`}
                        aria-label={n.pinned ? "Unpin note" : "Pin note"}
                      >
                        <span className="flex flex-col items-center gap-0.5">
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M14.5 3.5l6 6-3 1.5-4 4V21l-2-2-4-4-3 3-1-1 3-3-4-4-2-2h6.5l4-4 1.5-3z"
                              fill="white"
                            />
                          </svg>
                          <span className="text-[11px] leading-none">
                            {n.pinned ? t("unpin", langCode) : t("pin", langCode)}
                          </span>
                        </span>
                      </button>
                    )}
                    {/* Red Delete button — sits behind the row, revealed
                        by the leftward swipe of the foreground content. */}
                    {isSwipeable && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteUserDecoy(n.id);
                        }}
                        className="absolute inset-y-0 right-0 w-[84px] flex items-center justify-center text-white text-[15px] font-medium"
                        style={{ backgroundColor: "#FF3B30" }}
                        data-testid={`decoy-row-delete-${i}`}
                        aria-label="Delete note"
                      >
                        Delete
                      </button>
                    )}
                    <div
                      data-swipe-row={n.id}
                      className="relative bg-white"
                      onPointerDown={isSwipeable ? (e) => {
                        // Only react to primary button / single touch.
                        if (e.pointerType === "mouse" && e.button !== 0) return;
                        handleSwipeStart(n.id, e.clientX, e.clientY, e.currentTarget, e.pointerId);
                      } : undefined}
                      onPointerMove={isSwipeable ? (e) => handleSwipeMove(n.id, e.clientX, e.clientY) : undefined}
                      onPointerUp={isSwipeable ? () => handleSwipeEnd(n.id) : undefined}
                      onPointerCancel={isSwipeable ? () => handleSwipeEnd(n.id) : undefined}
                    >
                      <button
                        type="button"
                        onClick={handleRowClick}
                        className="w-full text-left px-4 py-3 active:bg-neutral-100 transition-colors"
                        data-testid={`decoy-row-${i}`}
                      >
                        <p
                          className="text-[16px] font-semibold text-black leading-tight truncate"
                          data-testid={`decoy-row-title-${i}`}
                        >
                          {titleText}
                        </p>
                        <p className="mt-1 text-[14px] text-neutral-500 truncate">
                          <span className="text-neutral-700">{dateLabels[i]}</span>
                          {effectivePreview ? <span className="mx-1">·</span> : null}
                          {effectivePreview}
                        </p>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {/* iOS-style "No Results" placeholder — shown only when the
            magician/spectator typed something into the search bar and
            zero decoy notes matched. Mirrors the empty state from real
            iOS Notes search. */}
        {!apocalypseWiped && isSearchActive && groupedDecoys.length === 0 && (
          <div
            className="mt-16 flex items-center justify-center"
            data-testid="decoy-search-no-results"
          >
            <p className="text-[17px] text-neutral-500">{t("no_results", langCode)}</p>
          </div>
        )}
      </div>

      {/* Legacy bottom compose toolbar removed — compose (+) now lives
          in the fixed bottom search bar next to the pill. See the
          <SearchBarBottom /> block near the end of this render. */}

      {/* ---------- Notes Apocalypse — flicker / pulse overlay ---------- */}
      {/* Two stacked overlays: a hue-rotate "scanline" layer and a brief
          color-burn flash. Both are CSS-driven so they don't trigger any
          extra React renders. They're invisible (opacity 0) when the
          phase is "idle", "emptied", or "rebooting", and pulse during
          all in-trick phases ("wipe" → "haunt") via the keyframes
          defined below. */}
      <div
        className="pointer-events-none absolute inset-0 mix-blend-screen"
        data-apocalypse-overlay="rgb"
        data-apocalypse-phase={apocalypsePhase}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0"
        data-apocalypse-overlay="flash"
        data-apocalypse-phase={apocalypsePhase}
        aria-hidden="true"
      />

      {/* "Haunt" hint — invisible to the spectator, only readable by the
          magician up close. Once the resolve is done it whispers the
          dismiss instruction so the magician knows the gesture is
          armed. Stays at 4% opacity so it reads as render artifact. */}
      {apocalypsePhase === "haunt" && (
        <div
          className="pointer-events-none absolute bottom-16 inset-x-0 flex justify-center"
          aria-hidden="true"
        >
          <span className="text-[10px] tracking-[0.3em] text-black/[0.04] uppercase">
            shake to dismiss
          </span>
        </div>
      )}

      {/* ---------- Fake iOS push banners ----------
          Renders a stack of iOS-style notification banners at the top
          of the viewport. Each banner is appended by the scheduling
          timer and removed `pushDurationMs` later. CSS keyframes
          (.apoc-push) drive the slide-in + drift-out feel. The stack
          sits below the iOS status-bar zone, above the trick FX. */}
      {pushBanners.length > 0 && (
        <div
          className="fixed left-0 right-0 z-[55] flex flex-col items-center gap-1.5 px-3 pointer-events-none"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
          data-testid="apocalypse-push-stack"
          aria-hidden="true"
        >
          {pushBanners.slice(-6).map((b) => (
            <div
              key={b.id}
              className="apoc-push w-full max-w-[380px] rounded-2xl bg-[rgba(58,58,60,0.42)] backdrop-blur-[28px] backdrop-saturate-[1.8] text-white shadow-[0_10px_28px_rgba(0,0,0,0.35)] border border-white/10 px-3 py-2.5 flex items-start gap-2.5"
              data-testid={`apocalypse-push-banner-${b.id}`}
            >
              {/* Real iOS-style app icon (inline SVG, no external assets).
                  Falls back to the original letter-tile for unknown apps
                  so magician-defined custom names still render. */}
              <IOSAppIcon name={b.app} className="w-7 h-7" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[12px] font-semibold tracking-tight truncate">{b.app}</p>
                  <p className="text-[10px] text-white/55 shrink-0">now</p>
                </div>
                <p className="text-[12px] leading-tight font-medium truncate">{b.title}</p>
                {b.body && (
                  <p className="text-[11px] leading-tight text-white/75 truncate">{b.body}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------- iOS-style "Error" alert dialog (Pass-4) ----------
          Appears once the resolve phase scrolls back to the top, IF the
          magician has Settings → Apocalypse → Error dialog enabled.
          The full-screen backdrop blocks taps + scroll on the
          underlying notes list, while touchstart/touchend STILL bubble
          up to the outer container so the magician's clock-swipe
          gestures can encode a second card. The OK button is visually
          present but its only effect is a tiny jitter — the dialog is
          dismissed by a SHAKE, not a tap. */}
      {showErrorDialog && (
        <div
          className="fixed inset-0 z-[58] flex items-center justify-center bg-black/35 backdrop-blur-[1.5px]"
          data-testid="apocalypse-error-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="apoc-error-title"
          onClick={() => {
            // Every tap on the frozen dialog (backdrop, title, body, OK)
            // re-fires the error sound. Magician-facing feature: they
            // can tap-tap-tap the popup to make the "phone is dying"
            // beat feel more relentless while holding for the shake.
            try {
              playErrorTritone({
                volume: (apocalypseCfg.errorSoundVolume ?? 80) / 100,
              });
            } catch { /* ignore */ }
          }}
        >
          <div
            key={errorJitterKey}
            className="apoc-error-dialog w-[270px] rounded-[14px] bg-[rgba(248,248,248,0.92)] backdrop-blur-2xl text-center overflow-hidden shadow-[0_18px_36px_rgba(0,0,0,0.35)]"
            data-testid="apocalypse-error-dialog"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
            }}
          >
            <div className="px-5 pt-4 pb-3">
              <p
                id="apoc-error-title"
                className="text-[17px] font-semibold text-black leading-tight"
                data-testid="apocalypse-error-title"
              >
                {apocalypseCfg.errorTitle}
              </p>
              <p
                className="mt-1.5 text-[13px] text-black/85 leading-snug"
                data-testid="apocalypse-error-body"
              >
                {apocalypseCfg.errorBody}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { /* frozen: OK is a no-op so the popup
                  and everything under it stays perfectly still until
                  the spectator shakes the phone. */ }}
              className="block w-full text-[#007AFF] text-[17px] font-semibold py-2.5 border-t border-black/10 active:bg-black/[0.04]"
              data-testid="apocalypse-error-ok-button"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ---------- Post-shake flash overlay ----------
          Only mounts in the "flash" reset preset. A single full-screen
          white div whose visibility is toggled by `flashOn` from
          runResetSequence(). The blink count, on-duration and off-gap
          are all magician-tunable from Settings → Apocalypse. */}
      {flashOn && (
        <div
          className="fixed inset-0 z-[60] bg-white pointer-events-none"
          data-testid="apocalypse-flash-overlay"
          aria-hidden="true"
        />
      )}

      {/* Keyframes + per-phase animation hookups for the overlays + the
          glitched note titles. The flash palette comes from Settings as
          CSS custom properties on the outer container so the magician
          can recolor every flicker without a code change. */}
      <style>{`
        [data-apoc-root] {
          --apoc-flash-1: ${apocalypseCfg.flashColors[0]};
          --apoc-flash-2: ${apocalypseCfg.flashColors[1]};
          --apoc-flash-3: ${apocalypseCfg.flashColors[2]};
          --apoc-flash-4: ${apocalypseCfg.flashColors[3]};
        }
        @keyframes mr-apoc-rgb {
          0%   { transform: translate(0,0); filter: hue-rotate(0deg) saturate(1); opacity: 0.0; }
          20%  { transform: translate(-2px,0); filter: hue-rotate(40deg) saturate(2); opacity: 0.45; }
          40%  { transform: translate(3px,0); filter: hue-rotate(-50deg) saturate(2.4); opacity: 0.55; }
          60%  { transform: translate(-2px,1px); filter: hue-rotate(120deg) saturate(3); opacity: 0.6; }
          80%  { transform: translate(1px,-1px); filter: hue-rotate(-90deg) saturate(2); opacity: 0.45; }
          100% { transform: translate(0,0); filter: hue-rotate(0deg) saturate(1); opacity: 0.0; }
        }
        @keyframes mr-apoc-flash {
          0%, 100% { background: transparent; opacity: 0; }
          30% { background: var(--apoc-flash-1); opacity: 0.18; }
          50% { background: var(--apoc-flash-2); opacity: 0.22; }
          70% { background: var(--apoc-flash-3); opacity: 0.55; }
          85% { background: var(--apoc-flash-4); opacity: 0.18; }
        }
        @keyframes mr-apoc-title-shift {
          0%, 100% { color: #000; text-shadow: none; }
          25% { color: var(--apoc-flash-1); text-shadow: 1px 0 0 var(--apoc-flash-2), -1px 0 0 var(--apoc-flash-4); }
          50% { color: var(--apoc-flash-2); text-shadow: 2px 0 0 var(--apoc-flash-1), -2px 0 0 var(--apoc-flash-4); }
          75% { color: #1a1a1a; text-shadow: -1px 0 0 var(--apoc-flash-1), 1px 0 0 var(--apoc-flash-2); }
        }
        @keyframes mr-apoc-extra-in {
          from { opacity: 0; transform: translateY(-8px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes mr-apoc-push-in {
          0%   { opacity: 0; transform: translateY(-24px) scale(0.96); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .apoc-push {
          animation: mr-apoc-push-in 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes mr-apoc-error-jitter {
          0% { transform: translate(0, 0); }
          20% { transform: translate(-3px, 1px); }
          40% { transform: translate(3px, -1px); }
          60% { transform: translate(-2px, 2px); }
          80% { transform: translate(2px, 0); }
          100% { transform: translate(0, 0); }
        }
        .apoc-error-dialog {
          animation: mr-apoc-error-jitter 220ms ease-out 1;
        }
        /* --- Micro screen-shake ------------------------------------
           Continuous sub-pixel jitter on the whole app root during the
           active-crash phases so the SCREEN itself looks like it's
           physically trembling — perfectly in sync with the vibration
           audio bed we loop underneath. Killed by 'data-apoc-frozen'
           the moment the error dialog raises so we get a hard cut
           from "shaking chaos" → "dead stillness" that lands like a
           whipcrack. Chose steps(11, end) over a smooth curve because
           smooth translate looks like a cinematic zoom-wobble; steps
           read as a real vibration motor. */
        @keyframes mr-apoc-microshake {
          0%   { transform: translate3d(0, 0, 0); }
          9%   { transform: translate3d(-1px, 0.6px, 0); }
          18%  { transform: translate3d(1.2px, -0.8px, 0); }
          27%  { transform: translate3d(-0.8px, 1px, 0); }
          36%  { transform: translate3d(1px, 0.5px, 0); }
          45%  { transform: translate3d(-1.4px, -0.6px, 0); }
          54%  { transform: translate3d(0.8px, 1.2px, 0); }
          63%  { transform: translate3d(-1px, -1px, 0); }
          72%  { transform: translate3d(1.3px, 0.4px, 0); }
          81%  { transform: translate3d(-0.5px, 0.9px, 0); }
          90%  { transform: translate3d(0.9px, -0.5px, 0); }
          100% { transform: translate3d(0, 0, 0); }
        }
        [data-apoc-root][data-apocalypse-phase="wipe"],
        [data-apoc-root][data-apocalypse-phase="spawn"],
        [data-apoc-root][data-apocalypse-phase="scramble"],
        [data-apoc-root][data-apocalypse-phase="flicker"],
        [data-apoc-root][data-apocalypse-phase="resolve"] {
          animation: mr-apoc-microshake 220ms steps(11, end) infinite;
          will-change: transform;
        }
        /* Hard kill for the shake once the error dialog has raised —
           "freeze wherever" is the requested contract. Overrides the
           phase-based animation via specificity + !important. */
        [data-apoc-root][data-apoc-frozen="true"] {
          animation: none !important;
          transform: none !important;
        }
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="wipe"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="spawn"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="scramble"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="flicker"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="resolve"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="error"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="reGlitch"],
        [data-apocalypse-overlay="rgb"][data-apocalypse-phase="haunt"] {
          animation: mr-apoc-rgb 420ms steps(8, end) infinite;
          background: linear-gradient(180deg,
            rgba(255,0,80,0.09) 0%,
            rgba(0,255,200,0.09) 50%,
            rgba(255,0,80,0.09) 100%);
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="wipe"] {
          animation: mr-apoc-flash 360ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="spawn"] {
          animation: mr-apoc-flash 320ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="scramble"] {
          animation: mr-apoc-flash 280ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="flicker"] {
          animation: mr-apoc-flash 300ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="resolve"] {
          animation: mr-apoc-flash 900ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="error"] {
          animation: mr-apoc-flash 620ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="reGlitch"] {
          animation: mr-apoc-flash 540ms steps(5, end) infinite;
        }
        [data-apocalypse-overlay="flash"][data-apocalypse-phase="haunt"] {
          animation: mr-apoc-flash 700ms steps(5, end) infinite;
        }
        [data-apocalypse-phase="wipe"] [data-testid^="decoy-row-title-"],
        [data-apocalypse-phase="spawn"] [data-testid^="apoc-extra-title-"],
        [data-apocalypse-phase="scramble"] [data-testid^="apoc-extra-title-"],
        [data-apocalypse-phase="flicker"] [data-testid^="decoy-row-title-"],
        [data-apocalypse-phase="resolve"] [data-testid^="decoy-row-title-"],
        [data-apocalypse-phase="flicker"] [data-testid^="apoc-extra-title-"],
        [data-apocalypse-phase="resolve"] [data-testid^="apoc-extra-title-"],
        [data-apocalypse-phase="error"] [data-testid^="apoc-extra-title-"],
        [data-apocalypse-phase="reGlitch"] [data-testid^="apoc-extra-title-"],
        [data-apocalypse-phase="haunt"] [data-testid^="apoc-extra-title-"] {
          animation: mr-apoc-title-shift 380ms steps(6, end) infinite;
        }
        .apoc-extra-row {
          animation: mr-apoc-extra-in 260ms ease-out both;
          overflow: hidden;
        }

        /* ---------- Apocalypse "error dialog" freeze ----------
           Once the iOS-style Error alert is shown, LITERALLY every
           visual animation under the root freezes on its current
           frame — flicker overlays, title-shifts, error-dialog jitter,
           push-banner slides — until the spectator shakes the phone
           (which clears showErrorDialog and un-freezes the tree).
           The 'animation-play-state: paused' rule freezes in-flight
           animations at their exact current progress point, giving
           the whole UI a single still-frame look that matches the
           "system halted" vibe of the error popup. Sound is left
           running per the magician's request. */
        [data-apoc-frozen="true"],
        [data-apoc-frozen="true"] *,
        [data-apoc-frozen="true"] *::before,
        [data-apoc-frozen="true"] *::after {
          animation-play-state: paused !important;
          transition: none !important;
        }
      `}</style>
      {/* Phantom Peek overlay — appears for the magician's
          peek-trigger duration once the "Notes" h1 is tapped
          and a (+) note's body had been silently captured during
          a recent swipe-delete. Subtle but readable: light-grey,
          large, fixed near the bottom edge so a glance during the
          phone-handover motion catches the word. Pointer-events
          off so it never blocks a re-tap of the title. */}
      {peekText && (
        <div
          className="pointer-events-none fixed left-0 right-0 z-40 px-6 text-center"
          style={{
            bottom: "12vh",
            color: "rgba(120,120,128,0.55)",
            fontSize: "44px",
            lineHeight: 1.05,
            fontWeight: 600,
            letterSpacing: "-0.5px",
            textShadow: "0 1px 0 rgba(255,255,255,0.6)",
            animation: "mr-peek-fade 220ms ease-out both",
            wordBreak: "break-word",
          }}
          data-testid="peek-overlay"
        >
          {peekText}
        </div>
      )}
      <style jsx="true">{`
        @keyframes mr-peek-fade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ---------- Fixed bottom search bar ----------
          Native iOS Notes 2024/2025 layout: search pill lives at the
          bottom of the screen next to a rounded-square compose button.
          On focus the pill glides up to sit just under the top nav
          (via a `bottom` transition against 100dvh so the keyboard-
          aware viewport keeps it consistent), and a "Cancel" affordance
          fades in while the compose square fades out. The scrim behind
          it dims the notes list to focus attention exactly like Apple's
          own search overlay. Hidden entirely during Apocalypse so the
          fake crash isn't interrupted. */}
      {apocalypsePhase === "idle" && !showErrorDialog && (
        <>
          {/* Dim scrim behind the search bar when focused. Tapping it
              cancels search + blurs the input, mirroring iOS. */}
          <div
            className="fixed inset-0 z-30 bg-black transition-opacity duration-300 ease-out"
            style={{
              opacity: searchFocused ? 0.22 : 0,
              pointerEvents: searchFocused ? "auto" : "none",
            }}
            aria-hidden="true"
            // Prevent the mousedown from stealing focus off the input,
            // otherwise the blur-race unmounts the scrim before our
            // onClick fires and the query never gets cleared.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              hapticTap();
              setSearchQuery("");
              setSearchFocused(false);
              searchInputRef.current?.blur();
            }}
            data-testid="decoy-search-scrim"
          />

          <div
            className="fixed left-0 right-0 z-40 transition-[bottom] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{
              // Native iOS Notes behaviour: while focused the search
              // pill sits *directly above* the software keyboard so
              // the user always sees what they're typing. We track
              // the keyboard height via visualViewport (see kbOffset
              // effect above) and pin the bar to that offset. When
              // unfocused it slides back down to the bottom edge.
              bottom: searchFocused ? `${kbOffset}px` : "0px",
            }}
            data-testid="decoy-search-bottom-bar"
          >
            {/* Frosted-glass material — this is what gives the iOS
                "everything behind the bar becomes hazy" effect. The
                container is full-bleed so scrolled notes and the
                iCloud banner alike get blurred as they pass under it.
                backdrop-blur-xl ≈ ~24px, backdrop-saturate boosts the
                colour behind to look richer, matching Apple's UIKit
                `UIBlurEffect(style: .systemMaterial)`. */}
            <div
              className="px-4 pt-2 pb-3 flex items-center gap-2 bg-[#F2F2F7]/70 backdrop-blur-xl backdrop-saturate-150"
              style={{
                paddingBottom: `calc(env(safe-area-inset-bottom) + 12px)`,
              }}
            >
              {/* Search pill — matches iOS with leading magnifier and
                  a trailing mic icon when empty, or a filled ✕ clear
                  button when the field has content. */}
              <div
                className="flex-1 flex items-center gap-2 bg-neutral-200/80 rounded-[10px] px-3 py-2 backdrop-blur"
                data-testid="decoy-search-bar"
                onClick={() => searchInputRef.current?.focus()}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" stroke="#8e8e93" strokeWidth="2"/>
                  <path d="M20 20l-3.2-3.2" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    hapticTap();
                    setSearchFocused(true);
                    // Snap the notes-list container to the top so the
                    // sticky "Notes" header + any search-force result
                    // render at the top of the visible area (rather
                    // than at whatever scrollTop the spectator left
                    // the folder at). We deliberately do NOT touch
                    // `window.scrollY` here — iOS Safari owns that
                    // scroll while the keyboard is opening, and any
                    // fight causes the input to blur / typed text to
                    // vanish.
                    if (listScrollRef.current) {
                      listScrollRef.current.scrollTop = 0;
                    }
                  }}
                  onBlur={() => setSearchFocused(false)}
                  placeholder={t("search", langCode)}
                  aria-label={t("search", langCode)}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  enterKeyHint="search"
                  data-testid="decoy-search-input"
                  className="flex-1 bg-transparent border-0 outline-none text-[15px] text-black placeholder:text-neutral-500 leading-none p-0 min-w-0"
                />
                {isSearchActive ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchQuery("");
                      searchInputRef.current?.focus();
                    }}
                    className="shrink-0 p-0 leading-none"
                    aria-label="Clear search"
                    data-testid="decoy-search-clear"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" fill="#8e8e93"/>
                      <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="#8e8e93"
                    aria-hidden="true"
                    data-testid="decoy-search-mic"
                  >
                    <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/>
                    <path d="M18 12a6 6 0 0 1-12 0H4.5a7.5 7.5 0 0 0 6.75 7.46V22h1.5v-2.54A7.5 7.5 0 0 0 19.5 12H18z"/>
                  </svg>
                )}
              </div>

              {/* Compose button (native iOS "square.and.pencil"). Fades
                  out while the search is focused so the pill can breathe,
                  and the Cancel affordance takes its slot. */}
              {!searchFocused ? (
                <button
                  type="button"
                  onClick={openCompose}
                  className="shrink-0 w-11 h-11 rounded-[12px] bg-white flex items-center justify-center shadow-[0_0.5px_2px_rgba(0,0,0,0.08)] active:bg-neutral-100 transition-colors border-0 p-0"
                  aria-label="new note"
                  data-testid="decoy-add-bottom"
                >
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    {/* Native iOS SF-Symbol "square.and.pencil".
                        Rounded square with the top-right corner opened
                        up where a diagonal pencil crosses through. */}
                    <path
                      d="M15.5 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.5"
                      stroke="#000"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.586 2.586a2 2 0 0 1 2.828 2.828L12 14.828 8 16l1.172-4 9.414-9.414z"
                      stroke="#000"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  // Same blur-race guard as the scrim: prevent the input
                  // from losing focus during mousedown so this button
                  // stays mounted through mouseup/click. Without this
                  // the Cancel unmounts mid-tap and the newly-mounted
                  // compose button (at the same slot) fires instead,
                  // spawning /trick.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    hapticTap();
                    setSearchQuery("");
                    setSearchFocused(false);
                    searchInputRef.current?.blur();
                  }}
                  className="shrink-0 h-11 px-3 rounded-[12px] flex items-center justify-center text-[15px] text-[#FFB400] font-normal bg-transparent border-0"
                  data-testid="decoy-search-cancel"
                  aria-label="Cancel"
                >
                  {langCode.startsWith("nl") ? "Annuleer" : "Cancel"}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function yesterdayLabel(locale) {
  try {
    // Browsers with RelativeTimeFormat give "Yesterday" / "Hier" / "Gisteren" etc.
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    return rtf.format(-1, "day");
  } catch {
    return "Yesterday";
  }
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Parse the value stored from a <input type="datetime-local"> back into
 * a Date. The input emits ISO-without-zone strings like "2026-04-28T07:30"
 * which `new Date(...)` interprets as local time — which is what we want.
 * Returns null on invalid input so the caller falls back to auto labels.
 */
function parseDecoyTime(value) {
  if (!value || typeof value !== "string") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * Build a string of `len` cryptic-looking characters for the Apocalypse
 * glitch animation. Heavy on punctuation + mixed alphanumerics so the
 * cycling text reads as a "hacked terminal" rather than random words.
 */
const GLITCH_CHARS =
  "!@#$%^&*<>?/|\\[]{}-=+~_ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
function randomGlitchString(len) {
  if (!len || len <= 0) return "";
  let out = "";
  for (let i = 0; i < len; i++) {
    out += GLITCH_CHARS.charAt(Math.floor(Math.random() * GLITCH_CHARS.length));
  }
  return out;
}

/**
 * Shuffle the magician's custom title pool and take the first `n`. If
 * the pool is shorter than `n` (e.g. they only filled in 3 entries but
 * asked for 10 extras), repeat the pool cyclically with a fresh shuffle
 * each cycle so the visible list still feels varied.
 */
function pickApocalypseExtras(n, pool) {
  const source = (pool || []).map((s) => String(s || "").trim()).filter(Boolean);
  if (n <= 0 || source.length === 0) return [];
  const shuffle = (arr) => {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  };
  const out = [];
  while (out.length < n) {
    out.push(...shuffle(source));
  }
  return out.slice(0, n);
}

/**
 * Format a magician-set datetime the way Apple Notes would render it in
 * the folder list: clock time if "today", relative weekday/yesterday for
 * recent dates, or a short "10 Apr 2026"-style stamp otherwise.
 */
function formatRowDateLabel(d, now, locale) {
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return capitalize(yesterdayLabel(locale));
  }
  // Within the last week → show weekday name
  const diffMs = now.getTime() - d.getTime();
  const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  if (diffMs > 0 && diffMs < ONE_WEEK_MS) {
    return d.toLocaleDateString(locale, { weekday: "long" });
  }
  return d.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}
